import React, { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import { authStorage } from '../utils/authStorage';
import axios from 'axios';
import common from '../utils/common';
import { fetchInvestmentDetails, fetchCoinGeckoPrices, fetchLPs, fetchParticipants } from '../features/api/apiSlice';
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import './ntc-branding.css';

const LPDashboard = () => {
  const dispatch = useAppDispatch();
  
  // Redux selectors for API data
  const { investmentDetails: reduxInvestmentDetails, coinGeckoData: reduxCoinGeckoData, lps: reduxLPs, participants: reduxParticipants, loading: apiLoading } = useAppSelector((state) => state.api);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Investment data state
  const [investmentData, setInvestmentData] = useState({
    amountInvested: 0,
    totalProjects: 0,
    releasedProjects: 0,
    releasedCount: 0,
    unreleasedAmount: 0,
    unreleasedCount: 0,
    overallROI: 0,
    projectsPC: 0,
    isLoading: true
  });

  // Investment details and filters
  const [investmentDetails, setInvestmentDetails] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [coinGeckoData, setCoinGeckoData] = useState([]);
  const [lps, setLPs] = useState([]);
  const [selectedLP, setSelectedLP] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [spotPurchaseDetails, setSpotPurchaseDetails] = useState([]);
  const [tokenSales, setTokenSales] = useState([]);
  const [spotTokenSales, setSpotTokenSales] = useState([]);
  const [spotCoinGeckoData, setSpotCoinGeckoData] = useState([]);
  const [filters, setFilters] = useState({
    ongoing: true,
    completed: true,
    unreleased: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [collapseDetail, setCollapseDetail] = useState(false);
  const [lpInfoExpanded, setLpInfoExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showLPDropdown, setShowLPDropdown] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [expandProject, setExpandProject] = useState(false);
  const [projectShow, setProjectShow] = useState(false);
  const [viewSalesOnly, setViewSalesOnly] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projTokensSold, setProjTokensSold] = useState('');
  const [projSoldAmount, setProjSoldAmount] = useState('');
  const [projSaleDate, setProjSaleDate] = useState(null);
  const [projSaleComment, setProjSaleComment] = useState('');
  const [expandSpot, setExpandSpot] = useState(false);
  const [spotSaleShow, setSpotSaleShow] = useState(false);
  const [selectedTokenData, setSelectedTokenData] = useState(null);
  const [spotTokensSold, setSpotTokensSold] = useState('');
  const [spotSoldAmount, setSpotSoldAmount] = useState('');
  const [spotSaleDate, setSpotSaleDate] = useState(null);
  const [spotSaleComment, setSpotSaleComment] = useState('');
  const [insertSpotToken, setInsertSpotToken] = useState(false);
  const [spotTokens, setSpotTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState('');
  const [spotBuyingPrice, setSpotBuyingPrice] = useState('');
  const [spotPurchaseAmount, setSpotPurchaseAmount] = useState('');
  const [spotTotalTokens, setSpotTotalTokens] = useState('');
  const [spotPurchaseDate, setSpotPurchaseDate] = useState(null);
  const [spotPurchaseBy, setSpotPurchaseBy] = useState('');
  const [spotPurchaseComment, setSpotPurchaseComment] = useState('');
  const [knowQuantity, setKnowQuantity] = useState(false);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const [tokenSearchTerm, setTokenSearchTerm] = useState('');
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
  const [participantSearchTerm, setParticipantSearchTerm] = useState('');
  const [isSavingSpotPurchase, setIsSavingSpotPurchase] = useState(false);
  
  // Custom Alert State
  const [customAlert, setCustomAlert] = useState({
    show: false,
    message: '',
    type: 'error' // 'error', 'success', 'warning', 'info'
  });
  const [isDirectTokenValidated, setIsDirectTokenValidated] = useState(false);
  const [isDirectTokenValid, setIsDirectTokenValid] = useState(false);
  const [resolvedDirectLpId, setResolvedDirectLpId] = useState(null);
  const directAccessParams = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return {
        directAccessToken: params.get('access') || null
      };
    } catch {
      return { directAccessToken: null };
    }
  }, []);
  const { directAccessToken } = directAccessParams;
  const isDirectLpAccess = !!directAccessToken && isDirectTokenValid;

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    document.documentElement.classList.toggle('minimenu');
    localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', !sidebarCollapsed ? 'menu-mini-theme' : '');
  };

  // Custom Alert Functions
  const showAlert = (message, type = 'error') => {
    setCustomAlert({
      show: true,
      message: message,
      type: type
    });
    // Auto-hide after 4 seconds for success, 5 seconds for errors
    setTimeout(() => {
      setCustomAlert(prev => ({ ...prev, show: false }));
    }, type === 'success' ? 4000 : 5000);
  };

  const hideAlert = () => {
    setCustomAlert(prev => ({ ...prev, show: false }));
  };

  // Restore sidebar state
  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    }
  }, []);

  useEffect(() => {
    if (!directAccessToken) {
      setIsDirectTokenValidated(true);
      setIsDirectTokenValid(false);
      return;
    }
    const validateDirectToken = async () => {
      try {
        const baseUrl = common.Data.API_URL;
        const response = await axios.get(`${baseUrl}/v1/resolve-lp-dashboard-access/${encodeURIComponent(directAccessToken)}`);
        if (response?.data?.participant_id) {
          setResolvedDirectLpId(String(response.data.participant_id));
          setIsDirectTokenValid(true);
          authStorage.setDirectLink('1');
        } else {
          setIsDirectTokenValid(false);
        }
      } catch (error) {
        console.error('Direct LP token validation failed:', error);
        setIsDirectTokenValid(false);
      } finally {
        setIsDirectTokenValidated(true);
      }
    };
    validateDirectToken();
  }, [directAccessToken]);

  useEffect(() => {
    if (directAccessToken && isDirectTokenValidated && !isDirectTokenValid) {
      window.location.href = '/';
    }
  }, [directAccessToken, isDirectTokenValidated, isDirectTokenValid]);

  // Select LP when LPs are loaded
  useEffect(() => {
    if (directAccessToken && (!isDirectTokenValidated || !isDirectTokenValid)) return;
    if (lps.length > 0 && !selectedLP) {
      try {
        if (isDirectLpAccess && resolvedDirectLpId) {
          const directLP = lps.find(c => String(c.participant_id) === String(resolvedDirectLpId));
          if (directLP) {
            setSelectedLP(directLP);
            return;
          }
          setSelectedLP(null);
          return;
        }
        const loginData = authStorage.getLoginData();
        let lp_login = null;
        
        if (loginData) {
          // Find LP matching logged-in user's ID
          lp_login = lps.find(c => c.participant_id == loginData.id);
        }
        
        // If no match found, use first LP
        if (!lp_login) {
          lp_login = lps[0];
        }
        
        setSelectedLP(lp_login);
      } catch (error) {
        console.error('Error selecting LP:', error);
        if (lps.length > 0) {
          if (isDirectLpAccess && resolvedDirectLpId) {
            const directLP = lps.find(c => String(c.participant_id) === String(resolvedDirectLpId));
            if (directLP) {
              setSelectedLP(directLP);
            }
            return;
          } else {
            setSelectedLP(lps[0]);
          }
        }
      }
    }
  }, [lps, selectedLP, isDirectLpAccess, resolvedDirectLpId, directAccessToken, isDirectTokenValidated, isDirectTokenValid]);

  // Helper function to process investment data for LP dashboard
  const processLPInvestmentData = (data, tokenSalesData) => {
    if (!data || data.length === 0) {
      setInvestmentData({
        amountInvested: 0,
        totalProjects: 0,
        releasedProjects: 0,
        releasedCount: 0,
        unreleasedAmount: 0,
        unreleasedCount: 0,
        overallROI: 0,
        projectsPC: 0,
        isLoading: false
      });
      return;
    }
    
      let amountInvested = 0;
      let releasedProjects = 0;
      let releasedCount = 0;
      let unreleasedAmount = 0;
      let unreleasedCount = 0;
      let overallROI = 0;
      
    data.forEach(inv => {
        amountInvested += Math.round(inv.allocation_amount);
        
        if (inv.status === 'ongoing' || inv.status === 'completed') {
          releasedProjects += Math.round(inv.allocation_amount);
          releasedCount++;
          if (inv.ntc_roi) {
            overallROI += parseFloat(inv.allocation_amount) * (parseFloat(inv.ntc_roi) / 100);
          }
        } else if (inv.status === 'unreleased') {
          unreleasedAmount += Math.round(inv.allocation_amount);
          unreleasedCount++;
        }
      });
    
    // Calculate Projects PC (Principal Covered)
    const projectsPC = tokenSalesData.reduce((sum, sale) => sum + parseFloat(sale.sale_amount || 0), 0);
      
      setInvestmentData({
        amountInvested,
      totalProjects: data.length,
        releasedProjects,
        releasedCount,
        unreleasedAmount,
        unreleasedCount,
        overallROI: amountInvested > 0 ? (overallROI / amountInvested) * 100 : 0,
      projectsPC,
        isLoading: false
      });
  };

  // Fetch investment data when selectedLP changes
  useEffect(() => {
    if (selectedLP && selectedLP.participant_id) {
      const lpId = selectedLP.participant_id;
      
      // Clear any existing data first to ensure fresh load
      setInvestmentDetails([]);
      setFilteredProjects([]);
      setInvestmentData(prev => ({ ...prev, isLoading: true }));
      
      // Fetch investment data and use result directly
      const fetchLPData = async () => {
        try {
          const result = await dispatch(fetchInvestmentDetails(lpId));
          
          // Use the result directly instead of waiting for Redux state update
          // This prevents using stale data from investor dashboard
          if (fetchInvestmentDetails.fulfilled.match(result)) {
            const investmentData = result.payload;
            setInvestmentDetails(investmentData);
          } else {
            setInvestmentData(prev => ({ ...prev, isLoading: false }));
          }
        } catch (error) {
          console.error('Error fetching LP investment data:', error);
          setInvestmentData(prev => ({ ...prev, isLoading: false }));
        }
      };
      
      fetchLPData();
      
      // Fetch spot purchases
      const baseUrl = common.Data.API_URL;
      axios.get(`${baseUrl}/v1/get-lp-spot-purchase/${lpId}`)
        .then((response) => {
          if (response.data && response.data.length > 0) {
            setSpotPurchaseDetails(response.data);
            // Fetch CoinGecko prices for spot purchases
            const coingeckoSymbols = response.data
              .map(item => item.coingecko_symbol)
              .filter(symbol => symbol && symbol !== '')
              .join(',');
            if (coingeckoSymbols) {
              axios.get(`https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${coingeckoSymbols}`)
                .then((priceResponse) => {
                  const updatedSpotPurchases = response.data.map(item => {
                    const coin = priceResponse.data.find(c => c.id === item.coingecko_symbol);
                    return {
                      ...item,
                      current_price: coin?.current_price || 0
                    };
                  });
                  setSpotPurchaseDetails(updatedSpotPurchases);
                  setSpotCoinGeckoData(priceResponse.data);
                })
                .catch((error) => {
                  console.error('Error fetching spot token prices:', error);
                });
            }
          } else {
            setSpotPurchaseDetails([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching spot purchases:', error);
          setSpotPurchaseDetails([]);
        });
      
      // Fetch token sales (project token sales)
      axios.get(`${baseUrl}/v1/get-token-sales/${lpId}`)
        .then((response) => {
          const tokenSalesData = response.data || [];
          setTokenSales(tokenSalesData);
          
          // Process investment data with token sales once both are loaded
          // This will be handled by the useEffect that watches investmentDetails and tokenSales
        })
        .catch((error) => {
          console.error('Error fetching token sales:', error);
          setTokenSales([]);
        });
      
      // Fetch spot token sales
      axios.get(`${baseUrl}/v1/get-spot-token-sales/${lpId}`)
        .then((response) => {
          setSpotTokenSales(response.data || []);
        })
        .catch((error) => {
          console.error('Error fetching spot token sales:', error);
          setSpotTokenSales([]);
        });
    }
  }, [selectedLP, dispatch]);

  // Process investment data when both investmentDetails and tokenSales are available
  useEffect(() => {
    if (investmentDetails.length > 0 || (investmentDetails.length === 0 && !investmentData.isLoading)) {
      processLPInvestmentData(investmentDetails, tokenSales);
    }
  }, [investmentDetails, tokenSales]);

  // Fetch LPs and participants
  useEffect(() => {
    dispatch(fetchLPs());
    dispatch(fetchParticipants());
  }, [dispatch]);

  // Update LPs from Redux
  useEffect(() => {
    if (reduxLPs) {
      setLPs(reduxLPs);
    }
  }, [reduxLPs]);

  // Update participants from Redux and filter to only show those from mst_lp_info
  useEffect(() => {
    if (reduxParticipants && lps.length > 0) {
      // Filter participants to only show those that are in mst_lp_info (LPs)
      const lpParticipantIds = lps.map(lp => lp.participant_id);
      const filtered = reduxParticipants.filter(participant => 
        lpParticipantIds.includes(participant.id)
      );
      setFilteredParticipants(filtered);
      setParticipants(filtered);
    } else if (reduxParticipants && lps.length === 0) {
      // If LPs haven't loaded yet, wait
      setParticipants([]);
      setFilteredParticipants([]);
    }
  }, [reduxParticipants, lps]);

  // Calculate spot purchase stats
  const calculateSpotStats = () => {
    let spotAmountInvested = 0;
    let spotReturns = 0;
    
    spotPurchaseDetails.forEach(item => {
      spotAmountInvested += Math.round(parseFloat(item.amount) || 0);
      if (item.current_price) {
        const currentPrice = parseFloat(item.current_price);
        const tokenPrice = parseFloat(item.token_price);
        const tokens = parseFloat(item.no_of_tokens);
        spotReturns += (currentPrice - tokenPrice) * tokens;
      }
    });
    
    const spotRealised = spotTokenSales.reduce((sum, sale) => sum + parseFloat(sale.selling_amount || 0), 0);
    const spotCurrentValue = spotAmountInvested + spotReturns;
    const spotUnrealised = spotCurrentValue - spotRealised;
    
    return {
      spotAmountInvested,
      spotReturns,
      spotRealised,
      spotCurrentValue,
      spotUnrealised
    };
  };


  // Update local state when Redux data changes
  // Don't use Redux state updates - we use the fetch result directly in the useEffect above
  // This prevents cross-dashboard data leakage from investor dashboard
  // The investmentDetails state is set directly from the fetch result, not from Redux

  // Handle LP selection change
  const handleLPChange = (lpParticipantId) => {
    if (isDirectLpAccess) return;
    const newSelectedLP = lps.find(lp => lp.participant_id == lpParticipantId);
    if (newSelectedLP) {
      setSelectedLP(newSelectedLP);
      // Reset investment details when LP changes
      setInvestmentDetails([]);
      setFilteredProjects([]);
    }
  };

  const copyDirectLPLink = async (lp) => {
    try {
      const baseUrl = common.Data.API_URL;
      const response = await axios.get(`${baseUrl}/v1/get-lp-dashboard-access-link/${lp.participant_id}`);
      const token = response?.data?.token;
      if (!token) {
        throw new Error('No token returned');
      }
      const directUrl = `${window.location.origin}/lp-dashboard?access=${encodeURIComponent(token)}`;
      await navigator.clipboard.writeText(directUrl);
      showAlert(`Direct link copied for ${lp.name}`, 'success');
    } catch (error) {
      console.error('Failed to copy LP direct link:', error);
      showAlert('Unable to copy direct link', 'error');
    }
  };

  // Fetch CoinGecko prices using Redux
  const fetchCoinGeckoPricesFunc = async (projects) => {
    try {
      const coingeckoSymbols = projects
        .map(inv => inv.coingecko_symbol)
        .filter(symbol => symbol && symbol !== '')
        .join(',');
      
      if (coingeckoSymbols) {
        dispatch(fetchCoinGeckoPrices(coingeckoSymbols));
      }
    } catch (error) {
      console.error('Error fetching CoinGecko data:', error);
    }
  };

  // Update CoinGecko data from Redux
  useEffect(() => {
    if (reduxCoinGeckoData) {
      setCoinGeckoData(reduxCoinGeckoData);
    }
  }, [reduxCoinGeckoData]);

  // Filter and sort projects
  const filterProjects = async () => {
    let filtered = [...investmentDetails];
    
    // Filter by status
    filtered = filtered.filter(inv => {
      if (filters.ongoing && inv.status === 'ongoing') return true;
      if (filters.completed && inv.status === 'completed') return true;
      if (filters.unreleased && inv.status === 'unreleased') return true;
      return false;
    });
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(inv => 
        inv.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort by status: completed, ongoing, unreleased
    const statusOrder = { 'completed': 1, 'ongoing': 2, 'unreleased': 3 };
    filtered = filtered.sort((a, b) => {
      const orderA = statusOrder[a.status] || 999;
      const orderB = statusOrder[b.status] || 999;
      return orderA - orderB;
    });
    
    setFilteredProjects(filtered);
    setShowAllProjects(false); // Reset show all when filters change
    await fetchCoinGeckoPricesFunc(filtered);
  };

  useEffect(() => {
    if (investmentDetails.length > 0) {
    filterProjects();
    }
  }, [filters, searchTerm, investmentDetails]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilters && !event.target.closest('.position-relative')) {
        setShowFilters(false);
      }
      if (showLPDropdown && !event.target.closest('.lp-dropdown-container')) {
        setShowLPDropdown(false);
      }
      if (showTokenDropdown && !event.target.closest('.token-dropdown-container')) {
        setShowTokenDropdown(false);
        setTokenSearchTerm('');
      }
      if (showParticipantDropdown && !event.target.closest('.participant-dropdown-container')) {
        setShowParticipantDropdown(false);
        setParticipantSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters, showLPDropdown, showTokenDropdown, showParticipantDropdown]);

  const handleSignout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  // Copy wallet address to clipboard
  const copyWalletAddress = async () => {
    if (selectedLP?.wallet_erc20) {
      try {
        await navigator.clipboard.writeText(selectedLP.wallet_erc20);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = selectedLP.wallet_erc20;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
    }
  };

  // Calculate project data for expanded view
  const getProjectData = (inv) => {
    const totalTokens = inv.token_price ? parseFloat(Math.round(inv.allocation_amount) / parseFloat(inv.token_price)).toFixed(2) : '';
    const releasePer = inv.release_per ? parseFloat(inv.release_per).toFixed(2) : '';
    const tokensReleased = inv.token_price && inv.release_per ? 
      parseFloat(Math.round(inv.allocation_amount) / parseFloat(inv.token_price) * parseFloat(inv.release_per) / 100).toFixed(2) : '';
    const marketPrice = getCurrentPrice(inv.coingecko_symbol);
    const projectId = inv.proj_id || inv.id;
    const projectSales = tokenSales.filter(x => x.proj_id == projectId);
    const tokensSold = projectSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
    const realisedAmount = projectSales.reduce((sum, sale) => sum + parseFloat(sale.sale_amount || 0), 0);
    const remainingTokens = tokensReleased ? parseFloat(tokensReleased) - tokensSold : 0;
    const unrealisedAmount = remainingTokens > 0 && marketPrice.price !== 'N/A' ? 
      parseFloat(marketPrice.price.replace('$', '')) * remainingTokens : 0;
    
    return {
      id: projectId,
      projectName: `${inv.name} ${inv.chain ? `(${inv.chain})` : ''}`,
      allocationAmount: `$${Math.round(inv.allocation_amount)}`,
      status: inv.status,
      tokenPrice: inv.token_price ? `$${inv.token_price}` : '',
      totalTokens,
      marketPrice: marketPrice.price !== 'N/A' ? parseFloat(marketPrice.price.replace('$', '')) : 0,
      releasePer,
      tokensReleased,
      tokensSold,
      realisedAmount,
      unrealisedAmount,
      remainingTokens: remainingTokens || 0
    };
  };

  // Handle add sale
  const handleAddSale = (project) => {
    const projectData = getProjectData(project);
    setSelectedProject(projectData);
    setViewSalesOnly(false);
    setProjTokensSold('');
    setProjSoldAmount('');
    setProjSaleDate(null);
    setProjSaleComment('');
    setProjectShow(true);
  };

  // Handle view sales (when all tokens are sold)
  const handleViewSales = (project) => {
    const projectData = getProjectData(project);
    setSelectedProject(projectData);
    setViewSalesOnly(true);
    setProjectShow(true);
  };

  // Helper function to detect and render links in text
  const renderCommentWithLinks = (comment) => {
    if (!comment || comment === '-') return <span className="text-muted" style={{ fontSize: '12px' }}>-</span>;
    
    // URL regex pattern
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const parts = comment.split(urlPattern);
    
    return (
      <span style={{ fontSize: '12px' }}>
        {parts.map((part, index) => {
          if (part.match(urlPattern)) {
            return (
              <a
                key={index}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-decoration-none"
                style={{
                  fontSize: '12px',
                  wordBreak: 'break-all'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none';
                }}
              >
                {part}
              </a>
            );
          }
          return <span key={index} className="text-muted">{part}</span>;
        })}
      </span>
    );
  };

  // Save token sale
  const saveTokenSale = async () => {
    if (!selectedProject || !selectedLP) return;
    
    if (!projTokensSold || !projSoldAmount || !projSaleDate) {
      showAlert('Please fill all mandatory fields!', 'error');
      return;
    }

    try {
      const loginData = authStorage.getLoginData();
      const baseUrl = common.Data.API_URL;
      const body = {
        login_id: loginData.id,
        lp_id: selectedLP.participant_id,
        proj_id: selectedProject.id || selectedProject.proj_id,
        tokens_sold: parseFloat(projTokensSold),
        sale_amount: parseFloat(projSoldAmount),
        sale_date: moment(projSaleDate).format('YYYY-MM-DD'),
        comment: projSaleComment
      };

      await axios.post(`${baseUrl}/v1/save-token-sales`, body);
      showAlert('Sale saved successfully!', 'success');
      setProjectShow(false);
      // Refresh token sales
      if (selectedLP && selectedLP.participant_id) {
        const response = await axios.get(`${baseUrl}/v1/get-token-sales/${selectedLP.participant_id}`);
        setTokenSales(response.data || []);
        // Refresh investment details to update calculations
        dispatch(fetchInvestmentDetails(selectedLP.participant_id));
      }
    } catch (error) {
      console.error('Error saving token sale:', error);
      showAlert('Failed to save sale', 'error');
    }
  };

  // Get spot tokens for dropdown
  const getSpotTokens = async () => {
    try {
      const baseUrl = common.Data.API_URL;
      const response = await axios.get(`${baseUrl}/v1/get-spot-tokens`);
      setSpotTokens(response.data || []);
      setInsertSpotToken(true);
    } catch (error) {
      console.error('Error fetching spot tokens:', error);
      showAlert('Failed to fetch tokens', 'error');
    }
  };

  // Calculate tokens based on amount and buying price
  const calculateSpotTokens = (val, isBuyingPrice) => {
    let tt = 0;
    let bp = 0;
    
    if (knowQuantity) {
      // Calculate buying price instead
      if (isBuyingPrice) {
        bp = parseFloat(parseFloat(spotPurchaseAmount || 0) / parseFloat(val || 1)).toFixed(4);
        setSpotBuyingPrice(bp);
      } else {
        bp = parseFloat(parseFloat(spotPurchaseAmount || 0) / parseFloat(val || 1)).toFixed(4);
        setSpotBuyingPrice(bp);
      }
    } else {
      // Calculate total tokens
      if (isBuyingPrice) {
        tt = parseFloat(parseFloat(spotPurchaseAmount || 0) / parseFloat(val || 1)).toFixed(2);
        setSpotTotalTokens(tt);
      } else {
        tt = parseFloat(parseFloat(val || 0) / parseFloat(spotBuyingPrice || 1)).toFixed(2);
        setSpotTotalTokens(tt);
      }
    }
  };

  // Save spot purchase
  const saveSpotPurchase = async () => {
    if (!selectedLP || !selectedToken || !spotPurchaseAmount || !spotPurchaseDate) {
      showAlert('Please fill all mandatory fields!', 'error');
      return;
    }

    if (!knowQuantity && (!spotBuyingPrice || !spotTotalTokens)) {
      showAlert('Please provide buying price or total tokens!', 'error');
      return;
    }

    if (knowQuantity && !spotTotalTokens) {
      showAlert('Please provide total tokens!', 'error');
      return;
    }

    setIsSavingSpotPurchase(true);
    try {
      const loginData = authStorage.getLoginData();
      const baseUrl = common.Data.API_URL;
      const body = {
        lp_id: selectedLP.participant_id,
        token_id: selectedToken,
        amount: parseFloat(spotPurchaseAmount),
        token_price: parseFloat(spotBuyingPrice || 0),
        no_of_tokens: parseFloat(spotTotalTokens || 0),
        purchased_by: spotPurchaseBy || null,
        purchase_date: moment(spotPurchaseDate).format('YYYY-MM-DD'),
        comment: spotPurchaseComment || '',
        created_on: Date.now(),
        created_by: loginData.id
      };

      await axios.post(`${baseUrl}/v1/save-spot-purchase`, body);
      showAlert('Spot purchase saved successfully!', 'success');
      setInsertSpotToken(false);
      // Reset form
      setSelectedToken('');
      setSpotBuyingPrice('');
      setSpotPurchaseAmount('');
      setSpotTotalTokens('');
      setSpotPurchaseDate(null);
      setSpotPurchaseBy('');
      setSpotPurchaseComment('');
      setKnowQuantity(false);
      setShowTokenDropdown(false);
      setTokenSearchTerm('');
      setShowParticipantDropdown(false);
      setParticipantSearchTerm('');
      
      // Refresh spot purchase details
      if (selectedLP && selectedLP.participant_id) {
        const spotResponse = await axios.get(`${baseUrl}/v1/get-lp-spot-purchase/${selectedLP.participant_id}`);
        if (spotResponse.data && spotResponse.data.length > 0) {
          setSpotPurchaseDetails(spotResponse.data);
          // Fetch CoinGecko prices
          const coingeckoSymbols = spotResponse.data
            .map(item => item.coingecko_symbol)
            .filter(symbol => symbol && symbol !== '')
            .join(',');
          if (coingeckoSymbols) {
            axios.get(`https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${coingeckoSymbols}`)
              .then((priceResponse) => {
                const updatedSpotPurchases = spotResponse.data.map(item => {
                  const coin = priceResponse.data.find(c => c.id === item.coingecko_symbol);
                  return {
                    ...item,
                    current_price: coin?.current_price || 0
                  };
                });
                setSpotPurchaseDetails(updatedSpotPurchases);
                setSpotCoinGeckoData(priceResponse.data);
              })
              .catch((error) => {
                console.error('Error fetching spot token prices:', error);
              });
          }
        } else {
          setSpotPurchaseDetails([]);
        }
      }
    } catch (error) {
      console.error('Error saving spot purchase:', error);
      showAlert('Failed to save spot purchase', 'error');
    } finally {
      setIsSavingSpotPurchase(false);
    }
  };

  // Save spot token sale
  const saveSpotSale = async () => {
    if (!selectedTokenData || !selectedLP) return;
    
    if (!spotTokensSold || !spotSoldAmount || !spotSaleDate) {
      showAlert('Please fill all mandatory fields!', 'error');
      return;
    }

    try {
      const loginData = authStorage.getLoginData();
      const baseUrl = common.Data.API_URL;
      const body = {
        login_id: loginData.id,
        lp_id: selectedLP.participant_id,
        token_id: selectedTokenData.token_id,
        tokens_sold: parseFloat(spotTokensSold),
        selling_amount: parseFloat(spotSoldAmount),
        sale_date: moment(spotSaleDate).format('YYYY-MM-DD'),
        comment: spotSaleComment
      };

      await axios.post(`${baseUrl}/v1/save-spot-token-sales`, body);
      showAlert('Spot sale saved successfully!', 'success');
      setSpotSaleShow(false);
      // Refresh spot token sales
      if (selectedLP && selectedLP.participant_id) {
        const response = await axios.get(`${baseUrl}/v1/get-spot-token-sales/${selectedLP.participant_id}`);
        setSpotTokenSales(response.data || []);
        // Refresh spot purchase details to update current prices
        const spotResponse = await axios.get(`${baseUrl}/v1/get-lp-spot-purchase/${selectedLP.participant_id}`);
        if (spotResponse.data && spotResponse.data.length > 0) {
          const coingeckoSymbols = spotResponse.data
            .map(item => item.coingecko_symbol)
            .filter(symbol => symbol && symbol !== '')
            .join(',');
          if (coingeckoSymbols) {
            axios.get(`https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${coingeckoSymbols}`)
              .then((priceResponse) => {
                const updatedSpotPurchases = spotResponse.data.map(item => {
                  const coin = priceResponse.data.find(c => c.id === item.coingecko_symbol);
                  return {
                    ...item,
                    current_price: coin?.current_price || 0
                  };
                });
                setSpotPurchaseDetails(updatedSpotPurchases);
                setSpotCoinGeckoData(priceResponse.data);
              })
              .catch((error) => {
                console.error('Error fetching spot token prices:', error);
              });
          }
        }
      }
    } catch (error) {
      console.error('Error saving spot sale:', error);
      showAlert('Failed to save spot sale', 'error');
    }
  };


  // Calculate project metrics for expanded view
  const getProjectMetrics = (project) => {
    const projectSales = tokenSales.filter(sale => sale.proj_id == (project.proj_id || project.id));
    const tokensSold = projectSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
    const realisedAmount = projectSales.reduce((sum, sale) => sum + parseFloat(sale.sale_amount || 0), 0);
    
    const tokenPrice = parseFloat(project.token_price);
    const allocation = Math.round(project.allocation_amount);
    const totalTokens = tokenPrice > 0 ? allocation / tokenPrice : 0;
    const releasePercent = parseFloat(project.release_per || project.ntc_roi || 0);
    const tokensReleased = totalTokens * (releasePercent / 100);
    const remainingTokens = tokensReleased - tokensSold;
    
    const currentPrice = getCurrentPrice(project.coingecko_symbol);
    const marketPrice = currentPrice.price !== 'N/A' ? parseFloat(currentPrice.price.replace('$', '')) : 0;
    const unrealisedAmount = remainingTokens * marketPrice;
    
    const pcPercent = allocation > 0 ? ((realisedAmount - allocation) / allocation) * 100 : 0;
    
    return {
      totalTokens: totalTokens.toFixed(2),
      tokensReleased: tokensReleased.toFixed(2),
      tokensSold: tokensSold.toFixed(2),
      remainingTokens: remainingTokens.toFixed(2),
      realisedAmount: realisedAmount.toFixed(2),
      unrealisedAmount: unrealisedAmount.toFixed(2),
      marketPrice,
      pcPercent: pcPercent.toFixed(2)
    };
  };

  const getCurrentPrice = (coingeckoId) => {
    if (!coingeckoId || !coinGeckoData.length) return { price: 'N/A', color: 'text-muted' };
    const coin = coinGeckoData.find(c => c.id === coingeckoId);
    if (!coin) return { price: 'N/A', color: 'text-muted' };
    return { 
      price: `$${coin.current_price.toFixed(5)}`, 
      color: parseFloat(coin.current_price) > 0 ? 'text-success' : 'text-danger' 
    };
  };

         const calculateReturnPercent = (allocation, tokenPrice, coingeckoId) => {
           if (!tokenPrice || allocation === 0) return { percent: 'NaN', color: 'text-muted' };
           const totalTokens = allocation / parseFloat(tokenPrice);
           const currentPrice = getCurrentPrice(coingeckoId);
           if (currentPrice.price === 'N/A') return { percent: 'NaN', color: 'text-muted' };
           const currentPriceValue = parseFloat(currentPrice.price.replace('$', ''));
           const currentValue = totalTokens * currentPriceValue;
           const returnPercent = ((currentValue / allocation) - 1) * 100;
           return { 
             percent: isNaN(returnPercent) ? 'NaN' : returnPercent.toFixed(2), 
             color: returnPercent > 0 ? 'text-success' : returnPercent < 0 ? 'text-danger' : 'text-muted'
           };
         };

  return (
    <div className="nxl-wrapper">
      {/* Custom Alert */}
      {customAlert.show && (
        <div 
          className="position-fixed"
          style={{
            top: '24px',
            right: '24px',
            zIndex: 9999,
            minWidth: '380px',
            maxWidth: '450px',
            animation: 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
        >
          <div 
            className="card shadow-lg border-0"
            style={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: `2px solid ${
                customAlert.type === 'error' ? '#dc3545' : 
                customAlert.type === 'success' ? '#28a745' : 
                customAlert.type === 'warning' ? '#ffc107' : 
                '#17a2b8'
              }`,
              backgroundColor: '#ffffff',
              boxShadow: `0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)`
            }}
          >
            <div 
              className="card-body p-0"
              style={{
                position: 'relative'
              }}
            >
              {/* Colored Left Border */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '5px',
                  backgroundColor: customAlert.type === 'error' ? '#dc3545' : 
                                  customAlert.type === 'success' ? '#28a745' : 
                                  customAlert.type === 'warning' ? '#ffc107' : 
                                  '#17a2b8'
                }}
              ></div>

              <div className="d-flex align-items-start p-4">
                {/* Icon Container */}
                <div 
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: customAlert.type === 'error' ? '#fee' : 
                                    customAlert.type === 'success' ? '#e8f5e9' : 
                                    customAlert.type === 'warning' ? '#fff8e1' : 
                                    '#e0f7fa',
                    flexShrink: 0,
                    marginRight: '16px',
                    boxShadow: `0 4px 12px ${
                      customAlert.type === 'error' ? 'rgba(220, 53, 69, 0.2)' : 
                      customAlert.type === 'success' ? 'rgba(40, 167, 69, 0.2)' : 
                      customAlert.type === 'warning' ? 'rgba(255, 193, 7, 0.2)' : 
                      'rgba(23, 162, 184, 0.2)'
                    }`
                  }}
                >
                  <i 
                    className={`feather-${
                      customAlert.type === 'error' ? 'alert-circle' : 
                      customAlert.type === 'success' ? 'check-circle' : 
                      customAlert.type === 'warning' ? 'alert-triangle' : 
                      'info'
                    }`}
                    style={{ 
                      fontSize: '24px',
                      color: customAlert.type === 'error' ? '#dc3545' : 
                             customAlert.type === 'success' ? '#28a745' : 
                             customAlert.type === 'warning' ? '#f57c00' : 
                             '#17a2b8'
                    }}
                  ></i>
                </div>

                {/* Content */}
                <div className="flex-grow-1" style={{ paddingRight: '12px' }}>
                  <h6 
                    className="mb-2 fw-bold"
                    style={{ 
                      fontSize: '16px',
                      color: customAlert.type === 'error' ? '#dc3545' : 
                             customAlert.type === 'success' ? '#28a745' : 
                             customAlert.type === 'warning' ? '#f57c00' : 
                             '#17a2b8',
                      lineHeight: '1.3'
                    }}
                  >
                    {customAlert.type === 'error' ? 'Error' : 
                     customAlert.type === 'success' ? 'Success' : 
                     customAlert.type === 'warning' ? 'Warning' : 
                     'Information'}
                  </h6>
                  <p 
                    className="mb-0" 
                    style={{ 
                      fontSize: '14px', 
                      lineHeight: '1.5',
                      color: '#495057',
                      fontWeight: '400'
                    }}
                  >
                    {customAlert.message}
                  </p>
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  className="btn-close"
                  onClick={hideAlert}
                  aria-label="Close"
                  style={{
                    opacity: 0.5,
                    fontSize: '14px',
                    padding: '4px',
                    transition: 'all 0.2s ease',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.5';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                ></button>
              </div>

              {/* Progress Bar */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  backgroundColor: customAlert.type === 'error' ? '#dc3545' : 
                                  customAlert.type === 'success' ? '#28a745' : 
                                  customAlert.type === 'warning' ? '#ffc107' : 
                                  '#17a2b8',
                  animation: 'progressBar 5s linear forwards'
                }}
              ></div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          0% {
            transform: translateX(120%) scale(0.8);
            opacity: 0;
          }
          50% {
            transform: translateX(-10px) scale(1.02);
          }
          100% {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
        
        @keyframes progressBar {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        
        .position-fixed {
          animation-fill-mode: both;
        }
      `}</style>

      {/* Navigation Sidebar */}
      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed}
        toggleSidebar={toggleSidebar}
      />

      {/* Main Content */}
      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              {/* Sidebar Toggle Button */}
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">LP Dashboard</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  {isDirectLpAccess ? (
                    <span className="text-muted" style={{ cursor: 'default' }}>Home</span>
                  ) : (
                    <a href="/lp-dashboard">Home</a>
                  )}
                </li>
                <li className="breadcrumb-item">LP Dashboard</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center gap-3">
              {(() => {
                const loginData = (authStorage.getLoginData() || {});
                const userData = loginData;
                return userData && (
                  <div className="d-flex align-items-center gap-2 d-none d-md-flex">
                    <i className="feather-user text-primary"></i>
                    <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                      Welcome, {(() => {
                        if (participants.length > 0 && userData.id) {
                          const userParticipant = participants.find(p => p.participant_id == userData.id || p.id == userData.id);
                          return userParticipant?.name || userData.name || 'User';
                        }
                        return userData.name || 'User';
                      })()}
                    </span>
                  </div>
                );
              })()}
              <div className="page-header-right-items">
                <div className="d-flex d-md-none">
                  <a href="javascript:void(0)" className="page-header-right-close-toggle">
                    <i className="feather-arrow-left me-2"></i>
                    <span>Back</span>
                  </a>
                </div>
              </div>
              <div className="d-md-none d-flex align-items-center">
                <a href="javascript:void(0)" className="page-header-right-open-toggle">
                  <i className="feather-align-right fs-20"></i>
                </a>
              </div>
            </div>
          </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="row" style={{ display: 'flex', flexWrap: 'nowrap', transition: 'all 0.4s ease-in-out' }}>
            {/* Left Column - LP Info & Quick Stats */}
            <div 
              className="col-lg-4 mb-4"
              style={{
                opacity: expandProject ? 0 : 1,
                transform: expandProject ? 'translateX(-100%)' : 'translateX(0)',
                maxHeight: expandProject ? '0px' : '5000px',
                overflow: expandProject ? 'hidden' : 'visible',
                marginBottom: expandProject ? 0 : '1.5rem',
                pointerEvents: expandProject ? 'none' : 'auto',
                transition: 'opacity 0.4s ease-in-out, transform 0.4s ease-in-out, max-height 0.4s ease-in-out, margin-bottom 0.4s ease-in-out, flex 0.4s ease-in-out',
                width: expandProject ? 0 : 'auto',
                flex: expandProject ? '0 0 0%' : '0 0 33.333333%',
                paddingLeft: expandProject ? 0 : undefined,
                paddingRight: expandProject ? 0 : undefined,
                visibility: expandProject ? 'hidden' : 'visible'
              }}
            >
              {/* LP Info Card */}
              <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '12px', overflow: 'visible', position: 'relative' }}>
                <div className="card-header bg-white border-bottom" style={{ padding: '1.25rem', paddingRight: '200px' }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="feather-user me-2 text-primary"></i>
                      LP Info
                    </h5>
                  </div>
                </div>
                
                {/* LP Dropdown at right end */}
                {lps.length > 0 && (() => {
                    const loginData = (authStorage.getLoginData() || {});
                  const isAdmin = loginData.role == 1 || loginData.role == 6;
                  return isAdmin && !isDirectLpAccess ? (
                    <div className="lp-dropdown-container" style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}>
                      <div className="position-relative">
                        <button 
                          className="btn btn-outline-primary btn-sm d-flex align-items-center" 
                          type="button" 
                          onClick={() => setShowLPDropdown(!showLPDropdown)}
                          style={{ 
                            padding: '6px 12px',
                            fontSize: '13px',
                            fontWeight: '500',
                            gap: '8px',
                            minWidth: '180px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <i className="feather-user fs-13"></i>
                            <span>{selectedLP?.name || 'Choose LP...'}</span>
                          </div>
                          <i className={`feather-chevron-down fs-11 ${showLPDropdown ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}></i>
                        </button>
                        {showLPDropdown && (
                          <div 
                            className="bg-white border rounded shadow-lg position-absolute"
                            style={{ 
                              minWidth: '180px', 
                              top: '100%', 
                              right: '0', 
                              zIndex: 9999,
                              marginTop: '4px',
                              padding: '8px 0',
                              maxHeight: '300px',
                              overflowY: 'auto'
                            }}
                          >
                            <div className="px-3 py-2 border-bottom">
                              <h6 className="mb-0 fw-semibold text-dark fs-12">Select LP</h6>
                            </div>
                            {lps.map((lp) => (
                              <div 
                                key={lp.participant_id}
                                className={`px-3 py-2 ${selectedLP?.participant_id == lp.participant_id ? 'bg-light' : ''}`}
                                style={{ 
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease'
                                }}
                                onClick={() => {
                                  handleLPChange(lp.participant_id);
                                  setShowLPDropdown(false);
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedLP?.participant_id != lp.participant_id) {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedLP?.participant_id != lp.participant_id) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <i className={`feather-user me-2 ${selectedLP?.participant_id == lp.participant_id ? 'text-primary' : 'text-muted'} fs-13`}></i>
                                  <span className={`fw-medium ${selectedLP?.participant_id == lp.participant_id ? 'text-primary' : 'text-dark'} fs-12`}>
                                    {lp.name}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm ms-auto p-0 text-secondary"
                                    title="Copy direct dashboard link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyDirectLPLink(lp);
                                    }}
                                  >
                                    <i className="feather-link fs-12"></i>
                                  </button>
                                  {selectedLP?.participant_id == lp.participant_id && (
                                    <i className="feather-check ms-2 text-primary fs-12"></i>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null;
                })()}
                
                <div className="card-body" style={{ padding: '1.5rem', position: 'relative', paddingBottom: '3rem' }}>
                  {/* LP Name with Active Badge */}
                  <div className="mb-4 pb-3" style={{ borderBottom: '2px solid #f0f0f0' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2" style={{ alignItems: 'center' }}>
                        <div className="fw-bold" style={{ fontSize: '18px', color: '#2c3e50', lineHeight: '1.5' }}>
                          {selectedLP?.name || 'Limited Partner'}
                    </div>
                        <span className="badge bg-success px-2 py-1 d-flex align-items-center" style={{ fontSize: '10px', fontWeight: '500', height: 'fit-content' }}>
                          <i className="feather-check me-1"></i> Active
                        </span>
                    </div>
                    </div>
                    </div>
                  
                  {/* LP Details List */}
                  <div>
                    {/* Always visible fields */}
                    <div className="mb-4">
                      <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <i className="feather-dollar-sign me-1"></i>Investment Amount
                    </div>
                      <div className="fw-bold" style={{ fontSize: '18px', color: 'rgb(44, 62, 80)' }}>
                        {selectedLP?.invested_amount 
                          ? `$${parseFloat(selectedLP.invested_amount).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`
                          : `$${investmentData.amountInvested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        }
                    </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <i className="feather-trending-up me-1"></i>Net Investment Amount*
                      </div>
                      <div className="fw-bold" style={{ fontSize: '18px', color: '#17a2b8' }}>
                        {selectedLP?.invested_amount && selectedLP?.management_fee
                          ? `$${((parseFloat(selectedLP.invested_amount) * (100 - parseFloat(selectedLP.management_fee)) / 100)).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`
                          : `$${(investmentData.amountInvested * 0.98).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        }
                    </div>
                  </div>
                  
                    <div className="mb-4">
                      <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <i className="feather-trending-up me-1"></i>Overall ROI*
                      </div>
                      <div className="fw-bold" style={{ fontSize: '18px', color: investmentData.overallROI >= 0 ? '#28a745' : '#dc3545' }}>
                        ${investmentData.overallROI.toFixed(2)} 
                        <span className="ms-2" style={{ fontSize: '14px' }}>
                          ({investmentData.overallROI.toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                    
                    {/* Expandable fields */}
                    <div
                      style={{
                        maxHeight: lpInfoExpanded ? '1000px' : '0',
                        overflow: 'hidden',
                        transition: 'max-height 0.4s ease-in-out, opacity 0.3s ease-in-out',
                        opacity: lpInfoExpanded ? 1 : 0
                      }}
                    >
                      <div style={{ paddingTop: lpInfoExpanded ? '0.5rem' : '0', transition: 'padding-top 0.3s ease-in-out' }}>
                        <div className="mb-4">
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <i className="feather-file-text me-1"></i>Agreement
                          </div>
                          <div className="fw-bold" style={{ fontSize: '15px', color: '#2c3e50' }}>
                            {selectedLP?.agreement_details || 'N/A'}
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <i className="feather-wallet me-1"></i>Wallet Address
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="fw-bold text-break flex-grow-1" 
                              style={{ fontSize: '14px', color: '#2c3e50', fontFamily: 'monospace', wordBreak: 'break-all' }}
                            >
                              {selectedLP?.wallet_erc20 || 'N/A'}
                            </div>
                            {selectedLP?.wallet_erc20 && (
                              <button
                                className="btn btn-sm p-1 d-flex align-items-center justify-content-center"
                                onClick={copyWalletAddress}
                                style={{
                                  minWidth: '32px',
                                  height: '32px',
                                  backgroundColor: copied ? '#28a745' : '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '6px',
                                  color: copied ? '#fff' : '#6c757d',
                                  transition: 'all 0.3s ease',
                                  cursor: 'pointer',
                                  flexShrink: 0
                                }}
                                onMouseEnter={(e) => {
                                  if (!copied) {
                                    e.currentTarget.style.backgroundColor = '#e9ecef';
                                    e.currentTarget.style.borderColor = '#adb5bd';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!copied) {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                    e.currentTarget.style.borderColor = '#dee2e6';
                                  }
                                }}
                                title={copied ? 'Copied!' : 'Copy to clipboard'}
                              >
                                {copied ? (
                                  <i className="feather-check" style={{ fontSize: '16px' }}></i>
                                ) : (
                                  <i className="feather-copy" style={{ fontSize: '16px' }}></i>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <i className="feather-credit-card me-1"></i>Available Funds
                          </div>
                          <div className="fw-bold" style={{ fontSize: '18px', color: '#ffc107' }}>
                            {selectedLP?.invested_amount && investmentData.amountInvested
                              ? `$${(parseFloat(selectedLP.invested_amount) * (100 - parseFloat(selectedLP.management_fee || 2)) / 100 - investmentData.amountInvested).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`
                              : `$${(investmentData.amountInvested * 0.019).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            }
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <i className="feather-unlock me-1"></i>Realised in wallet ($)
                          </div>
                          <div className="fw-bold" style={{ fontSize: '18px', color: '#28a745' }}>
                            ${investmentData.releasedProjects.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                            <span className="text-muted ms-2 fw-normal" style={{ fontSize: '14px' }}>
                              ({selectedLP?.invested_amount 
                                ? ((investmentData.releasedProjects / parseFloat(selectedLP.invested_amount)) * 100).toFixed(2)
                                : investmentData.amountInvested > 0 
                                  ? ((investmentData.releasedProjects / investmentData.amountInvested) * 100).toFixed(2) 
                                  : 0
                              }%)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Floating Expand/Collapse Button - Always at bottom */}
                    <style>{`
                      @keyframes slowJump {
                        0%, 100% {
                          transform: translateX(-50%) translateY(0);
                        }
                        50% {
                          transform: translateX(-50%) translateY(-8px);
                        }
                      }
                      .show-more-button {
                        animation: slowJump 2s ease-in-out infinite;
                      }
                    `}</style>
                    <button
                      className="btn d-flex align-items-center justify-content-center show-more-button"
                      onClick={() => setLpInfoExpanded(!lpInfoExpanded)}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '1px solid #dee2e6',
                        background: '#ffffff',
                        color: '#6c757d',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        position: 'absolute',
                        bottom: '1rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.animation = 'none';
                        e.currentTarget.style.transform = 'translateX(-50%) translateY(-2px) scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#adb5bd';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.animation = 'slowJump 2s ease-in-out infinite';
                        e.currentTarget.style.transform = 'translateX(-50%) translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.backgroundColor = '#ffffff';
                        e.currentTarget.style.borderColor = '#dee2e6';
                      }}
                      title={lpInfoExpanded ? 'Show Less' : 'Show More'}
                    >
                      <i 
                        className={`feather-chevron-${lpInfoExpanded ? 'up' : 'down'}`}
                        style={{
                          fontSize: '18px',
                          transition: 'transform 0.3s ease',
                          fontWeight: 'normal',
                          color: '#6c757d'
                        }}
                      ></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">
                    <i className="feather-activity me-2"></i>Quick Stats
                  </h5>
                  
                  {(() => {
                    const spotStats = calculateSpotStats();
                    return (
                      <>
                        <div className="d-flex align-items-center gap-2 p-2 border rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#e3f2fd' }}>
                            <i className="feather-briefcase text-primary" style={{ fontSize: '18px' }}></i>
                    </div>
                    <div>
                            <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>{investmentData.totalProjects} (${investmentData.amountInvested.toLocaleString()})</div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Projects Invested</div>
                    </div>
                  </div>

                        <div className="d-flex align-items-center gap-2 p-2 border rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#e8f5e9' }}>
                            <i className="feather-unlock text-success" style={{ fontSize: '18px' }}></i>
                    </div>
                    <div>
                            <div className="fw-bold text-success" style={{ fontSize: '18px' }}>{investmentData.releasedCount} (${investmentData.releasedProjects.toLocaleString()})</div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Released Projects</div>
                    </div>
                  </div>

                        <div className="d-flex align-items-center gap-2 p-2 border rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#fff3cd' }}>
                            <i className="feather-lock text-warning" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div className="fw-bold text-warning" style={{ fontSize: '18px' }}>{investmentData.unreleasedCount} (${investmentData.unreleasedAmount.toLocaleString()})</div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Unreleased Projects</div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 p-2 border rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#d1ecf1' }}>
                            <i className="feather-percent text-info" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div className="fw-bold text-info" style={{ fontSize: '18px' }}>
                              ${investmentData.projectsPC ? investmentData.projectsPC.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') : '0.00'} 
                              <span className="text-muted ms-1" style={{ fontSize: '13px' }}>
                                ({investmentData.amountInvested > 0 ? ((investmentData.projectsPC / investmentData.amountInvested) * 100).toFixed(2) : 0}%)
                              </span>
                            </div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Projects PC</div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 p-2 border rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#f0f0f0' }}>
                            <i className="feather-shopping-bag text-muted" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>{spotPurchaseDetails.length} (${spotStats.spotAmountInvested.toLocaleString()})</div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Spot Purchases (Buying)</div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 p-2 border rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#f0f0f0' }}>
                            <i className="feather-trending-up text-muted" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>
                              ${spotStats.spotCurrentValue.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')} 
                              <span className="ms-1" style={{ fontSize: '13px', color: spotStats.spotReturns >= 0 ? '#28a745' : '#dc3545' }}>
                                ({spotStats.spotAmountInvested > 0 ? (spotStats.spotReturns >= 0 ? '+' : '') + ((spotStats.spotReturns / spotStats.spotAmountInvested) * 100).toFixed(2) : 0}%)
                              </span>
                            </div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Spot (Current Value)</div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 p-2 border rounded mb-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#e8f5e9' }}>
                            <i className="feather-check-circle text-success" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div className="fw-bold text-success" style={{ fontSize: '18px' }}>
                              ${spotStats.spotRealised.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')} 
                              <span className="text-muted ms-1" style={{ fontSize: '13px' }}>
                                ({spotStats.spotAmountInvested > 0 ? ((spotStats.spotRealised / spotStats.spotAmountInvested) * 100).toFixed(2) : 0}%)
                              </span>
                            </div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Spot (Realised)</div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 p-2 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px', backgroundColor: '#fff3cd' }}>
                            <i className="feather-clock text-warning" style={{ fontSize: '18px' }}></i>
                          </div>
                          <div>
                            <div className="fw-bold text-warning" style={{ fontSize: '18px' }}>
                              ${spotStats.spotUnrealised.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')} 
                              <span className="ms-1" style={{ fontSize: '13px', color: spotStats.spotUnrealised >= 0 ? '#28a745' : '#dc3545' }}>
                                ({spotStats.spotAmountInvested > 0 ? ((spotStats.spotUnrealised / spotStats.spotAmountInvested) * 100).toFixed(2) : 0}%)
                              </span>
                            </div>
                            <div className="text-muted" style={{ fontSize: '12px' }}>Spot (Unrealised)</div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Right Column - Project Investments */}
            <div 
              className={expandProject ? "col-lg-12" : "col-lg-8"}
              style={{
                transition: 'flex 0.4s ease-in-out',
                opacity: 1,
                visibility: 'visible',
                flex: expandProject ? '0 0 100%' : '0 0 66.666667%',
                transform: 'translateX(0)',
                marginLeft: 'auto',
                marginRight: 0,
                alignSelf: 'stretch',
                position: 'relative',
                right: 0,
                flexShrink: 0,
                maxWidth: '100%',
                overflow: 'hidden'
              }}
            >
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white border-bottom">
                  <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
                    <h5 className="mb-0 fw-bold">
                      Project Investments ({investmentData.totalProjects})
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                      {expandProject && (() => {
                        const loginData = (authStorage.getLoginData() || {});
                        const isAdmin = loginData.role == 1 || loginData.role == 6;
                        return isAdmin && lps.length > 0 && !isDirectLpAccess ? (
                          <div className="lp-dropdown-container position-relative">
                            <button 
                              className="btn btn-outline-primary btn-sm d-flex align-items-center" 
                              type="button" 
                              onClick={() => setShowLPDropdown(!showLPDropdown)}
                              style={{ 
                                padding: '6px 12px',
                                fontSize: '13px',
                                fontWeight: '500',
                                gap: '8px',
                                minWidth: '200px',
                                justifyContent: 'space-between'
                              }}
                            >
                              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                <i className="feather-user fs-13"></i>
                                <span>{selectedLP?.name || 'Choose LP...'}</span>
                              </div>
                              <i className={`feather-chevron-down fs-11 ${showLPDropdown ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}></i>
                            </button>
                            {showLPDropdown && (
                              <div 
                                className="bg-white border rounded shadow-lg position-absolute"
                                style={{ 
                                  minWidth: '200px', 
                                  top: '100%', 
                                  right: 0, 
                                  zIndex: 9999,
                                  marginTop: '4px',
                                  padding: '8px 0',
                                  maxHeight: '300px',
                                  overflowY: 'auto'
                                }}
                              >
                                <div className="px-3 py-2 border-bottom">
                                  <h6 className="mb-0 fw-semibold text-dark fs-12">Select LP</h6>
                                </div>
                                {lps.map((lp) => (
                                  <div 
                                    key={lp.participant_id}
                                    className={`px-3 py-2 ${selectedLP?.participant_id == lp.participant_id ? 'bg-light' : ''}`}
                                    style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                                    onClick={() => {
                                      handleLPChange(lp.participant_id);
                                      setShowLPDropdown(false);
                                    }}
                                    onMouseEnter={(e) => {
                                      if (selectedLP?.participant_id != lp.participant_id) {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (selectedLP?.participant_id != lp.participant_id) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }
                                    }}
                                  >
                                    <div className="d-flex align-items-center">
                                      <i className={`feather-user me-2 ${selectedLP?.participant_id == lp.participant_id ? 'text-primary' : 'text-muted'} fs-13`}></i>
                                      <span className={`fw-medium ${selectedLP?.participant_id == lp.participant_id ? 'text-primary' : 'text-dark'} fs-12`}>
                                        {lp.name}
                                      </span>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm ms-auto p-0 text-secondary"
                                    title="Copy direct dashboard link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyDirectLPLink(lp);
                                    }}
                                  >
                                    <i className="feather-link fs-12"></i>
                                  </button>
                                      {selectedLP?.participant_id == lp.participant_id && (
                                    <i className="feather-check ms-2 text-primary fs-12"></i>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                      {(() => {
                        const loginData = (authStorage.getLoginData() || {});
                        const isAdmin = loginData.role == 1 || loginData.role == 6;
                        return (isAdmin || isDirectLpAccess) ? (
                          <>
                            {!expandProject && (
                              <button 
                                type="button" 
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setExpandProject(!expandProject)}
                              >
                                <i className="feather-maximize-2 me-1 fs-11"></i>
                                Expand
                              </button>
                            )}
                            {expandProject && (
                              <button 
                                type="button" 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setExpandProject(!expandProject)}
                              >
                                <i className="feather-minimize-2 me-1 fs-11"></i>
                                Collapse
                              </button>
                            )}
                          </>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <div className={`d-flex align-items-center ${!expandProject ? 'gap-2 flex-wrap' : 'gap-3'}`}>
                    {/* Search Input */}
                    <div className="position-relative" style={{ width: !expandProject ? '200px' : '300px' }}>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0" style={{ padding: !expandProject ? '4px 8px' : '8px 12px' }}>
                          <i className={`feather-search text-muted ${!expandProject ? 'fs-12' : 'fs-14'}`}></i>
                        </span>
                    <input 
                      type="text" 
                          className="form-control border-start-0 bg-white"
                          placeholder={!expandProject ? "Search..." : "Search projects..."}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ 
                            borderLeft: 'none',
                            fontSize: !expandProject ? '12px' : '14px',
                            padding: !expandProject ? '4px 8px' : '8px 12px'
                          }}
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => setSearchTerm('')}
                            style={{ 
                              borderLeft: 'none', 
                              padding: !expandProject ? '4px 6px' : '8px',
                              fontSize: !expandProject ? '10px' : '12px'
                            }}
                            title="Clear search"
                          >
                            <i className={`feather-x ${!expandProject ? 'fs-10' : 'fs-12'}`}></i>
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Filter Dropdown */}
                    <div className="position-relative">
                      <button 
                        className="btn btn-outline-primary btn-sm d-flex align-items-center" 
                        type="button" 
                        onClick={() => setShowFilters(!showFilters)}
                        style={{ 
                          padding: !expandProject ? '4px 8px' : '6px 12px',
                          fontSize: !expandProject ? '10px' : '13px',
                          fontWeight: '500',
                          gap: !expandProject ? '4px' : '8px'
                        }}
                      >
                        <i className={`feather-filter ${!expandProject ? 'fs-10' : 'fs-13'}`}></i>
                        {expandProject && <span>Filters</span>}
                        <i className={`feather-chevron-down ${!expandProject ? 'fs-9' : 'fs-11'} ${showFilters ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}></i>
                      </button>
                      {showFilters && (
                        <div 
                          className="bg-white border rounded shadow-lg position-absolute"
                          style={{ 
                            minWidth: !expandProject ? '240px' : '280px', 
                            top: '100%', 
                            right: '0', 
                            zIndex: 9999,
                            marginTop: '4px',
                            padding: !expandProject ? '6px 0' : '8px 0'
                          }}
                        >
                          <div className={`px-3 ${!expandProject ? 'py-1' : 'py-2'} border-bottom`}>
                            <h6 className={`mb-0 fw-semibold text-dark ${!expandProject ? 'fs-11' : 'fs-12'}`}>Investment Status</h6>
                          </div>
                          
                          <div className={`px-3 ${expandProject ? 'py-1' : 'py-2'}`}>
                            <div className="form-check d-flex align-items-center">
                              <input
                                className="form-check-input me-3"
                                type="checkbox"
                                id="ongoing-lp-filter"
                                checked={filters.ongoing}
                                onChange={(e) => setFilters(prev => ({ ...prev, ongoing: e.target.checked }))}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <label className="form-check-label d-flex align-items-center w-100" htmlFor="ongoing-lp-filter">
                                <i className={`feather-clock me-2 text-primary ${!expandProject ? 'fs-11' : 'fs-13'}`}></i>
                                <span className={`text-primary fw-medium ${!expandProject ? 'fs-11' : 'fs-12'}`}>Ongoing</span>
                              </label>
                            </div>
                          </div>
                          
                          <div className={`px-3 ${expandProject ? 'py-1' : 'py-2'}`}>
                            <div className="form-check d-flex align-items-center">
                              <input
                                className="form-check-input me-3"
                                type="checkbox"
                                id="completed-lp-filter"
                                checked={filters.completed}
                                onChange={(e) => setFilters(prev => ({ ...prev, completed: e.target.checked }))}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <label className="form-check-label d-flex align-items-center w-100" htmlFor="completed-lp-filter">
                                <i className={`feather-check-circle me-2 text-success ${!expandProject ? 'fs-11' : 'fs-13'}`}></i>
                                <span className={`text-success fw-medium ${!expandProject ? 'fs-11' : 'fs-12'}`}>Completed</span>
                              </label>
                            </div>
                          </div>
                          
                          <div className={`px-3 ${expandProject ? 'py-1' : 'py-2'}`}>
                            <div className="form-check d-flex align-items-center">
                              <input
                                className="form-check-input me-3"
                                type="checkbox"
                                id="unreleased-lp-filter"
                                checked={filters.unreleased}
                                onChange={(e) => setFilters(prev => ({ ...prev, unreleased: e.target.checked }))}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <label className="form-check-label d-flex align-items-center w-100" htmlFor="unreleased-lp-filter">
                                <i className={`feather-pause-circle me-2 text-danger ${!expandProject ? 'fs-11' : 'fs-13'}`}></i>
                                <span className={`text-danger fw-medium ${!expandProject ? 'fs-11' : 'fs-12'}`}>Unreleased</span>
                              </label>
                            </div>
                          </div>
                          
                          <div className="border-top my-2"></div>
                          
                          <div className={`px-3 ${expandProject ? 'py-1' : 'py-2'}`}>
                            <button 
                              className="btn btn-outline-secondary btn-sm w-100"
                              onClick={() => {
                                setFilters({
                                  ongoing: true,
                                  completed: false,
                                  unreleased: false
                                });
                                setShowFilters(false);
                              }}
                              style={{ fontSize: !expandProject ? '10px' : '12px' }}
                            >
                              <i className={`feather-refresh-cw me-2 ${!expandProject ? 'fs-10' : 'fs-11'}`}></i>
                              Reset Filters
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card-body p-0">
                  <div className="table-responsive" style={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <table className="table table-hover mb-0" style={{ minWidth: expandProject ? '1200px' : 'auto', width: '100%' }}>
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12`} style={{ minWidth: expandProject ? '120px' : '140px' }}>Project Name</th>
                          <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '100px' }}>Allocation($)</th>
                          <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-center`} style={{ minWidth: '90px' }}>Status</th>
                          <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '110px' }}>Token Price($)</th>
                          {expandProject && <th className={`border-0 px-2 py-2 fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '100px' }}>Total Tokens</th>}
                          <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '110px' }}>Released(%)</th>
                          <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '120px' }}>Market Price($)</th>
                          {expandProject && <th className={`border-0 px-2 py-2 fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '100px' }}>Tokens Sold</th>}
                          {expandProject && <th className={`border-0 px-2 py-2 fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '100px' }}>Realised($)</th>}
                          {expandProject && <th className={`border-0 px-2 py-2 fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '110px' }}>Unrealised($)</th>}
                          <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`} style={{ minWidth: '80px' }}>% PC*</th>
                          {expandProject && <th className={`border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center`} style={{ minWidth: '100px' }}>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {investmentData.isLoading ? (
                          <tr>
                            <td colSpan={expandProject ? 11 : 8} className="text-center py-4">
                              <span className="text-muted fs-12">No project found</span>
                            </td>
                          </tr>
                        ) : filteredProjects.length === 0 ? (
                          <tr>
                            <td colSpan={expandProject ? 11 : 8} className="text-center py-4">
                              <span className="text-muted fs-12">No project found</span>
                            </td>
                          </tr>
                        ) : (
                          (showAllProjects ? filteredProjects : filteredProjects.slice(0, 10)).map((inv, index) => {
                          const currentPriceInfo = getCurrentPrice(inv.coingecko_symbol);
                          const releasedPercent = inv.ntc_roi ? parseFloat(inv.ntc_roi).toFixed(2) : '0.00';
                          const tokenPrice = parseFloat(inv.token_price).toFixed(6);
                          const allocatedAmount = Math.round(inv.allocation_amount);
                          const returnInfo = calculateReturnPercent(allocatedAmount, inv.token_price, inv.coingecko_symbol);
                          const projectData = getProjectData(inv);
                          const loginData = (authStorage.getLoginData() || {});
                          const isAdmin = loginData.role == 1 || loginData.role == 6;

                          return (
                            <tr key={index} className="border-bottom">
                              <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'}`}>
                                {inv.status === 'ongoing' || inv.status === 'completed' ? (
                                  <a href={`/investor-dashboard?${inv.name}`} target="_blank" className="fw-semibold text-primary text-decoration-none fs-12" style={{ textDecoration: 'underline' }}>
                                    {projectData.projectName}
                                  </a>
                                ) : (
                                  <span className="fw-semibold text-dark fs-12">{projectData.projectName}</span>
                                )}
                              </td>
                              <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                <span className="fw-semibold text-dark fs-12">{projectData.allocationAmount}</span>
                              </td>
                              <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-center`}>
                                <span className={`badge fs-11 ${inv.status === 'completed' ? 'bg-success' : inv.status === 'ongoing' ? 'bg-primary' : 'bg-warning'}`}>
                                  {inv.status || 'N/A'}
                                </span>
                              </td>
                              <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                <span className="fw-medium text-dark fs-12">{projectData.tokenPrice || tokenPrice || ''}</span>
                              </td>
                              {expandProject && (
                                <td className="px-2 py-2 text-end">
                                  <span className="fw-medium text-dark fs-12">{projectData.totalTokens || ''}</span>
                              </td>
                              )}
                              <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                <span className="fw-medium text-dark fs-12">
                                  {projectData.tokensReleased && projectData.releasePer 
                                    ? `${projectData.tokensReleased} (${projectData.releasePer}%)` 
                                    : (releasedPercent ? releasedPercent + '%' : '')}
                                </span>
                              </td>
                              <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                {parseFloat(projectData.marketPrice) > 0 ? (
                                  <span className="fw-medium fs-12" style={{ 
                                    color: parseFloat(projectData.marketPrice) > parseFloat(inv.token_price || 0) ? '#28a745' : '#dc3545' 
                                  }}>
                                    ${projectData.marketPrice.toFixed(5)}
                                  </span>
                                ) : (
                                  <span className="fw-medium text-muted fs-12">{currentPriceInfo.price !== 'N/A' ? currentPriceInfo.price : ''}</span>
                                )}
                              </td>
                              {expandProject && (
                                <td className="px-2 py-2 text-end">
                                  {projectData.releasePer ? (
                                    <span className="fw-medium text-dark fs-12">{projectData.tokensSold.toFixed(2)}</span>
                                  ) : (
                                    <span className="fw-medium text-dark fs-12"></span>
                                  )}
                                </td>
                              )}
                              {expandProject && (
                                <td className="px-2 py-2 text-end">
                                  {projectData.releasePer ? (
                                    <span className="fw-medium text-dark fs-12">${projectData.realisedAmount.toFixed(2)}</span>
                                  ) : (
                                    <span className="fw-medium text-dark fs-12"></span>
                                  )}
                                </td>
                              )}
                              {expandProject && (
                                <td className="px-2 py-2 text-end">
                                  {projectData.releasePer && parseFloat(projectData.marketPrice) > 0 ? (
                                    <span className="fw-medium text-dark fs-12">${projectData.unrealisedAmount.toFixed(2)}</span>
                                  ) : (
                                    <span className="fw-medium text-dark fs-12"></span>
                                  )}
                                </td>
                              )}
                              <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                {((inv.status === 'ongoing' || inv.status === 'completed') && projectData.releasePer) ? (
                                  <span className="fw-medium fs-12" style={{
                                    color: parseFloat((projectData.realisedAmount - parseFloat(projectData.allocationAmount.replace('$', ''))) * 100 / parseFloat(projectData.allocationAmount.replace('$', ''))).toFixed(2) >= 0 ? '#28a745' : '#dc3545'
                                  }}>
                                    {parseFloat((projectData.realisedAmount - parseFloat(projectData.allocationAmount.replace('$', ''))) * 100 / parseFloat(projectData.allocationAmount.replace('$', ''))).toFixed(2)}%
                                  </span>
                                ) : (
                                  <span className="fw-medium text-muted fs-12"></span>
                                )}
                              </td>
                              {expandProject && (
                                <td className="px-2 py-2 text-center">
                                  {(inv.status === 'ongoing' || inv.status === 'completed') && projectData.releasePer && isAdmin && loginData.id == 1 ? (
                                    projectData.remainingTokens > 0 ? (
                                      <button 
                                        className="btn btn-sm btn-outline-success"
                                        onClick={() => handleAddSale(inv)}
                                        title="Add sale"
                                      >
                                        <i className="feather-plus fs-11"></i>
                                        Add Sale
                                      </button>
                                    ) : (
                                      <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleViewSales(inv)}
                                        title="View all sales"
                                      >
                                        <i className="feather-eye fs-11"></i>
                                        View Sale
                                      </button>
                                    )
                                  ) : (
                                    <span></span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredProjects.length > 10 && (
                    <div className="card-footer bg-white border-top text-center py-3">
                      <style>{`
                        @keyframes slowJumpProjects {
                          0%, 100% {
                            transform: translateY(0);
                          }
                          50% {
                            transform: translateY(-6px);
                          }
                        }
                        .show-more-projects-button {
                          animation: slowJumpProjects 2s ease-in-out infinite;
                        }
                      `}</style>
                      <button
                        className="btn btn-sm btn-link text-decoration-none show-more-projects-button"
                        onClick={() => setShowAllProjects(!showAllProjects)}
                        style={{ 
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#007bff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.animation = 'none';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.animation = 'slowJumpProjects 2s ease-in-out infinite';
                        }}
                      >
                        {showAllProjects ? (
                          <>
                            <i className="feather-chevron-up me-1"></i>
                            Show Less
                          </>
                        ) : (
                          <>
                            <i className="feather-chevron-down me-1"></i>
                            Show More ({filteredProjects.length - 10} more)
                          </>
                        )}
                      </button>
                </div>
                  )}
              </div>
                   </div>

              {/* Spot Purchases Table */}
              {spotPurchaseDetails.length > 0 && (
                <div className="card shadow-sm border-0 mt-4">
                  <div className="card-header bg-white border-bottom">
                    <div className={`d-flex justify-content-between align-items-start w-100`}>
                      <div>
                        <h4 className="card-title mb-1 fw-bold text-dark d-flex align-items-center flex-wrap">
                          <i className="feather-shopping-bag me-2 text-primary"></i>
                          Spot Purchases ({spotPurchaseDetails.length})
                          {!expandProject && !insertSpotToken && (() => {
                            const loginData = (authStorage.getLoginData() || {});
                            const isAdmin = loginData.role == 1 || loginData.role == 6;
                            return isAdmin ? (
                              <button 
                                className="btn btn-sm btn-outline-secondary ms-2" 
                                onClick={() => setExpandProject(true)}
                              >
                                <i className="feather-maximize-2 me-1 fs-11"></i>
                                Expand
                              </button>
                            ) : null;
                          })()}
                          {expandProject && !insertSpotToken && (() => {
                            const loginData = (authStorage.getLoginData() || {});
                            const isAdmin = loginData.role == 1 || loginData.role == 6;
                            return isAdmin ? (
                              <button 
                                className="btn btn-sm btn-outline-primary ms-2" 
                                onClick={() => setExpandProject(false)}
                              >
                                <i className="feather-minimize-2 me-1 fs-11"></i>
                                Collapse
                              </button>
                            ) : null;
                          })()}
                        </h4>
                        <p className="text-muted mb-0 fs-12">Manage and track spot token purchases</p>
                      </div>
                      <div>
                        {(() => {
                          const loginData = (authStorage.getLoginData() || {});
                          const isAdmin = loginData.role == 1 || loginData.role == 6;
                          return isAdmin && !insertSpotToken ? (
                            <button 
                              className="btn btn-warning btn-sm"
                              onClick={getSpotTokens}
                            >
                              <i className="feather-plus me-1 fs-11"></i>
                              Add New Token
                            </button>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="card-body px-3 py-0">
                    {/* Add Spot Token Form - Show when insertSpotToken is true */}
                    {insertSpotToken ? (
                      <div className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                            <i className="feather-plus fs-5"></i>
                          </div>
                          <div className="ms-3">
                            <h5 className="mb-0 fw-bold text-dark">Add New Spot Purchase</h5>
                            <small className="text-muted">Enter spot purchase details for {selectedLP?.name || 'the selected LP'}</small>
                   </div>
                 </div>

                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2 text-dark fs-13">
                              <i className="feather-coins me-2 text-primary fs-12"></i>
                              Token <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative token-dropdown-container">
                              <button
                                type="button"
                                className="form-control text-start d-flex align-items-center justify-content-between"
                                onClick={() => {
                                  setShowTokenDropdown(!showTokenDropdown);
                                  setTokenSearchTerm('');
                                }}
                                style={{
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '13px',
                                  backgroundColor: 'white',
                                  cursor: 'pointer',
                                  minHeight: '38px'
                                }}
                              >
                                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                  <i className="feather-coins fs-13"></i>
                                  <span>
                                    {selectedToken 
                                      ? spotTokens.find(t => t.id == selectedToken) 
                                        ? `${spotTokens.find(t => t.id == selectedToken).token_name} (${spotTokens.find(t => t.id == selectedToken).token_symbol})`
                                        : 'Choose Token...'
                                      : 'Choose Token...'}
                                  </span>
                                </div>
                                <i className={`feather-chevron-down fs-11 ${showTokenDropdown ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}></i>
                              </button>
                              {showTokenDropdown && (
                                <div 
                                  className="bg-white border rounded shadow-lg position-absolute"
                                  style={{ 
                                    width: '100%', 
                                    top: '100%', 
                                    left: '0', 
                                    zIndex: 9999,
                                    marginTop: '4px',
                                    padding: '8px 0',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    borderRadius: '8px'
                                  }}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                >
                                  <div className="px-3 py-2 border-bottom">
                                    <h6 className="mb-2 fw-semibold text-dark fs-12">Select Token</h6>
                                    <div className="position-relative">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Search tokens..."
                                        value={tokenSearchTerm}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          setTokenSearchTerm(e.target.value);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onFocus={(e) => e.stopPropagation()}
                                        style={{
                                          fontSize: '12px',
                                          padding: '6px 30px 6px 10px',
                                          borderRadius: '6px',
                                          border: '1px solid #dee2e6'
                                        }}
                                        autoFocus
                                      />
                                      <i className="feather-search position-absolute" style={{ 
                                        right: '10px', 
                                        top: '50%', 
                                        transform: 'translateY(-50%)',
                                        fontSize: '14px',
                                        color: '#6c757d',
                                        pointerEvents: 'none'
                                      }}></i>
                                    </div>
                                  </div>
                                  <div 
                                    style={{ maxHeight: '200px', overflowY: 'auto' }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    {spotTokens
                                      .filter(token => {
                                        if (!tokenSearchTerm || tokenSearchTerm.trim() === '') return true;
                                        const searchLower = tokenSearchTerm.toLowerCase().trim();
                                        const tokenName = (token.token_name || '').toLowerCase();
                                        const tokenSymbol = (token.token_symbol || '').toLowerCase();
                                        return tokenName.includes(searchLower) || tokenSymbol.includes(searchLower);
                                      })
                                      .map((token) => (
                                        <div 
                                          key={token.id}
                                          className={`px-3 py-2 ${selectedToken == token.id ? 'bg-light' : ''}`}
                                          style={{ 
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s ease'
                                          }}
                                          onClick={() => {
                                            setSelectedToken(token.id);
                                            setShowTokenDropdown(false);
                                            setTokenSearchTerm('');
                                          }}
                                          onMouseEnter={(e) => {
                                            if (selectedToken != token.id) {
                                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (selectedToken != token.id) {
                                              e.currentTarget.style.backgroundColor = 'transparent';
                                            }
                                          }}
                                        >
                                          <div className="d-flex align-items-center">
                                            <i className={`feather-coins me-2 ${selectedToken == token.id ? 'text-primary' : 'text-muted'} fs-13`}></i>
                                            <span className={`fw-medium ${selectedToken == token.id ? 'text-primary' : 'text-dark'} fs-12`}>
                                              {token.token_name} ({token.token_symbol})
                                            </span>
                                            {selectedToken == token.id && (
                                              <i className="feather-check ms-auto text-primary fs-12"></i>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    {spotTokens.filter(token => {
                                      if (!tokenSearchTerm || tokenSearchTerm.trim() === '') return false;
                                      const searchLower = tokenSearchTerm.toLowerCase().trim();
                                      const tokenName = (token.token_name || '').toLowerCase();
                                      const tokenSymbol = (token.token_symbol || '').toLowerCase();
                                      return tokenName.includes(searchLower) || tokenSymbol.includes(searchLower);
                                    }).length === 0 && tokenSearchTerm && tokenSearchTerm.trim() !== '' && (
                                      <div className="px-3 py-3 text-center text-muted" style={{ fontSize: '12px' }}>
                                        No tokens found
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2 text-dark fs-13">
                              <i className="feather-dollar-sign me-2 text-primary fs-12"></i>
                              Buying Price <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.0001"
                              className="form-control"
                              placeholder="Enter buying price"
                              value={spotBuyingPrice}
                              onChange={(e) => {
                                setSpotBuyingPrice(e.target.value);
                                if (!knowQuantity && spotPurchaseAmount) {
                                  calculateSpotTokens(e.target.value, true);
                                }
                              }}
                              disabled={knowQuantity}
                              style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                            />
                            <div className="form-check mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="knowQuantity"
                                checked={knowQuantity}
                                onChange={(e) => {
                                  setKnowQuantity(e.target.checked);
                                  setSpotBuyingPrice('');
                                  setSpotTotalTokens('');
                                }}
                              />
                              <label className="form-check-label" htmlFor="knowQuantity" style={{ fontSize: '12px' }}>
                                <strong className="text-primary">I don't know the buying price</strong>
                              </label>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2 text-dark fs-13">
                              <i className="feather-dollar-sign me-2 text-primary fs-12"></i>
                              Amount ($) <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <span className="input-group-text fs-12" style={{ backgroundColor: '#f8f9fa' }}>$</span>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                placeholder="Enter amount"
                                value={spotPurchaseAmount}
                                onChange={(e) => {
                                  setSpotPurchaseAmount(e.target.value);
                                  if (!knowQuantity && spotBuyingPrice) {
                                    calculateSpotTokens(e.target.value, false);
                                  } else if (knowQuantity && spotTotalTokens) {
                                    calculateSpotTokens(e.target.value, false);
                                  }
                                }}
                                style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                              />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2 text-dark fs-13">
                              <i className="feather-hash me-2 text-primary fs-12"></i>
                              Total Tokens <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              placeholder="Enter total tokens"
                              value={spotTotalTokens}
                              onChange={(e) => {
                                setSpotTotalTokens(e.target.value);
                                if (knowQuantity && spotPurchaseAmount) {
                                  calculateSpotTokens(e.target.value, true);
                                }
                              }}
                              disabled={!knowQuantity}
                              style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: !knowQuantity ? '#e9ecef' : 'white' }}
                            />
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2 text-dark fs-13">
                              <i className="feather-calendar me-2 text-primary fs-12"></i>
                              Purchase Date <span className="text-danger">*</span>
                            </label>
                            <DatePicker
                              selected={spotPurchaseDate}
                              onChange={(date) => setSpotPurchaseDate(date)}
                              dateFormat="MMM dd, yyyy"
                              className="form-control"
                              placeholderText="Select purchase date"
                              wrapperClassName="w-100"
                              style={{
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '13px'
                              }}
                            />
                          </div>

                          <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2 text-dark fs-13">
                              <i className="feather-user me-2 text-primary fs-12"></i>
                              Purchased By
                            </label>
                            <div className="position-relative participant-dropdown-container">
                              <button
                                type="button"
                                className="form-control text-start d-flex align-items-center justify-content-between"
                                onClick={() => {
                                  setShowParticipantDropdown(!showParticipantDropdown);
                                }}
                                style={{
                                  borderRadius: '8px',
                                  border: '1px solid #dee2e6',
                                  padding: '10px 14px',
                                  fontSize: '14px',
                                  backgroundColor: 'white',
                                  cursor: 'pointer',
                                  minHeight: '42px',
                                  transition: 'all 0.2s ease'
                                }}
                                onFocus={(e) => {
                                  e.currentTarget.style.borderColor = '#667eea';
                                  e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                                }}
                                onBlur={(e) => {
                                  setTimeout(() => {
                                    e.currentTarget.style.borderColor = '#dee2e6';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }, 200);
                                }}
                              >
                                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                  <i className="feather-user fs-13"></i>
                                  <span>
                                    {spotPurchaseBy 
                                      ? filteredParticipants.find(p => p.participant_id == spotPurchaseBy) 
                                        ? filteredParticipants.find(p => p.participant_id == spotPurchaseBy).name
                                        : 'Select Participant...'
                                      : 'Select Participant...'}
                                  </span>
                                </div>
                                <i className={`feather-chevron-down fs-11 ${showParticipantDropdown ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}></i>
                              </button>
                              {showParticipantDropdown && (
                                <div 
                                  className="bg-white border rounded shadow-lg position-absolute"
                                  style={{ 
                                    width: '100%', 
                                    top: '100%', 
                                    left: '0', 
                                    zIndex: 9999,
                                    marginTop: '4px',
                                    padding: '8px 0',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    borderRadius: '8px'
                                  }}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                >
                                  <div className="px-3 py-2 border-bottom">
                                    <h6 className="mb-0 fw-semibold text-dark fs-12">Select Participant</h6>
                                  </div>
                                  <div 
                                    style={{ maxHeight: '250px', overflowY: 'auto' }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    {filteredParticipants.map((participant) => (
                                      <div 
                                        key={participant.participant_id}
                                        className={`px-3 py-2 ${spotPurchaseBy == participant.participant_id ? 'bg-light' : ''}`}
                                        style={{ 
                                          cursor: 'pointer',
                                          transition: 'background-color 0.2s ease'
                                        }}
                                        onClick={() => {
                                          setSpotPurchaseBy(participant.participant_id);
                                          setShowParticipantDropdown(false);
                                        }}
                                        onMouseEnter={(e) => {
                                          if (spotPurchaseBy != participant.participant_id) {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (spotPurchaseBy != participant.participant_id) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                          }
                                        }}
                                      >
                                        <div className="d-flex align-items-center">
                                          <i className={`feather-user me-2 ${spotPurchaseBy == participant.participant_id ? 'text-primary' : 'text-muted'} fs-13`}></i>
                                          <span className={`fw-medium ${spotPurchaseBy == participant.participant_id ? 'text-primary' : 'text-dark'} fs-12`}>
                                            {participant.name}
                                          </span>
                                          {spotPurchaseBy == participant.participant_id && (
                                            <i className="feather-check ms-auto text-primary fs-12"></i>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-12">
                            <label className="form-label fw-semibold mb-2 text-dark fs-13">
                              <i className="feather-message-circle me-2 text-primary fs-12"></i>
                              Comment (Optional)
                            </label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={spotPurchaseComment}
                              onChange={(e) => setSpotPurchaseComment(e.target.value)}
                              placeholder="Enter comment here..."
                              style={{ borderColor: '#dee2e6', fontSize: '13px', resize: 'none' }}
                            />
                          </div>
                        </div>
                        
                        <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
                          <button 
                            className="btn btn-outline-secondary btn-sm px-3"
                            onClick={() => {
                              setInsertSpotToken(false);
                              setSelectedToken('');
                              setSpotBuyingPrice('');
                              setSpotPurchaseAmount('');
                              setSpotTotalTokens('');
                              setSpotPurchaseDate(null);
                              setSpotPurchaseBy('');
                              setSpotPurchaseComment('');
                              setKnowQuantity(false);
                              setShowTokenDropdown(false);
                              setTokenSearchTerm('');
                            }}
                            disabled={isSavingSpotPurchase}
                          >
                            <i className="feather-x me-1 fs-11"></i>
                            Cancel
                          </button>
                          <button 
                            className="btn btn-primary btn-sm px-3"
                            onClick={saveSpotPurchase}
                            disabled={isSavingSpotPurchase}
                          >
                            {isSavingSpotPurchase ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="feather-save me-1 fs-11"></i>
                                Save Purchase
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive" style={{ maxWidth: '100%', overflowX: 'auto' }}>
                          <table className="table table-hover mb-0" style={{ width: '100%' }}>
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                          <tr>
                            <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-center`}>Token</th>
                            <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`}>Inv. Amount($)</th>
                            <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`}>Buying Price($)</th>
                            <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`}>Initial Holdings</th>
                            <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`}>Market Price($)</th>
                            {expandProject && <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-end">Tokens Sold</th>}
                            {expandProject && <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-end">Remaining Tokens</th>}
                            <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`}>Realised($)</th>
                            {expandProject && <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-end">Unrealised($)</th>}
                            <th className={`border-0 ${expandProject ? 'px-2 py-2' : 'px-3 py-2'} fw-semibold text-dark fs-12 text-end`}>% ROI</th>
                            {expandProject && <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center">Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {spotPurchaseDetails.map((spot, index) => {
                            const spotSales = spotTokenSales.filter(x => x.token_id == spot.token_id);
                            const tokensSold = spotSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
                            const realisedAmount = spotSales.reduce((sum, sale) => sum + parseFloat(sale.selling_amount || 0), 0);
                            const remainingTokens = parseFloat(spot.no_of_tokens || 0) - tokensSold;
                            const currentPrice = spot.current_price || (spotCoinGeckoData.find(c => c.id === spot.coingecko_symbol)?.current_price || 0);
                            const unrealisedAmount = remainingTokens > 0 && currentPrice > 0 ? remainingTokens * currentPrice : 0;
                            const roi = spot.per_roi || (spot.amount > 0 && currentPrice > 0 ? ((currentPrice - parseFloat(spot.token_price || 0)) / parseFloat(spot.token_price || 1)) * 100 : 0);
                            const loginData = (authStorage.getLoginData() || {});
                            const isAdmin = loginData.role == 1 || loginData.role == 6;
                            
                            return (
                              <tr key={index} className="border-bottom">
                                <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-center`}>
                                  <span className="fw-semibold text-dark fs-12">{spot.token_name || 'N/A'}</span>
                                </td>
                                <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className="fw-semibold text-dark fs-12">${parseFloat(spot.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </td>
                                <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className="fw-medium text-dark fs-12">${parseFloat(spot.token_price || 0).toFixed(2)}</span>
                                </td>
                                <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className="fw-medium text-dark fs-12">{parseFloat(spot.no_of_tokens || 0).toLocaleString()}</span>
                                </td>
                                <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  {currentPrice > 0 ? (
                                    <span className="fw-medium fs-12" style={{ 
                                      color: currentPrice > parseFloat(spot.token_price || 0) ? '#28a745' : '#dc3545' 
                                    }}>
                                      ${currentPrice.toFixed(3)}
                                    </span>
                                  ) : (
                                    <span className="fw-medium text-muted fs-12">N/A</span>
                                  )}
                                </td>
                                {expandProject && (
                                  <td className="px-2 py-2 text-end">
                                    <span className="fw-medium text-dark fs-12">{tokensSold.toFixed(2)}</span>
                                  </td>
                                )}
                                {expandProject && (
                                  <td className="px-2 py-2 text-end">
                                    <span className="fw-medium text-dark fs-12">{remainingTokens.toFixed(2)}</span>
                                  </td>
                                )}
                                <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  <span className="fw-medium text-dark fs-12">${realisedAmount.toFixed(2)}</span>
                                </td>
                                {expandProject && (
                                  <td className="px-2 py-2 text-end">
                                    {currentPrice > 0 ? (
                                      <span className="fw-medium text-dark fs-12">${unrealisedAmount.toFixed(2)}</span>
                                    ) : (
                                      <span className="fw-medium text-dark fs-12">-</span>
                                    )}
                                  </td>
                                )}
                                <td className={`${expandProject ? 'px-2 py-2' : 'px-3 py-2'} text-end`}>
                                  {currentPrice > 0 ? (
                                    <span className="fw-medium fs-12" style={{
                                      color: roi >= 0 ? '#28a745' : '#dc3545'
                                    }}>
                                      {roi.toFixed(2)}%
                                    </span>
                                  ) : (
                                    <span className="fw-medium text-muted fs-12">-</span>
                                  )}
                                </td>
                                {expandProject && isAdmin && loginData.id == 1 && (
                                  <td className="px-2 py-2 text-center">
                                    <button 
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => {
                                        setSelectedTokenData(spot);
                                        setSpotTokensSold('');
                                        setSpotSoldAmount('');
                                        setSpotSaleDate(null);
                                        setSpotSaleComment('');
                                        setSpotSaleShow(true);
                                      }}
                                      title="Add sale"
                                    >
                                      <i className="feather-plus fs-11"></i>
                                      Add Sale
                                    </button>
                                  </td>
                                )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                      </>
                    )}
                </div>
              </div>
              )}

              {/* Add Token Sale Modal */}
              <Modal
                show={projectShow}
                onHide={() => { setProjectShow(false); setViewSalesOnly(false); }}
                animation={true}
                size="lg"
                centered
                style={{ zIndex: 1055 }}
              >
                <style>{`
                  .modal.show .modal-header .btn-close {
                    filter: brightness(0) invert(1);
                    opacity: 1;
                  }
                `}</style>
                <Modal.Header 
                  closeButton 
                  style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderBottom: 'none',
                    padding: '1.25rem 1.5rem'
                  }}
                >
                  <Modal.Title style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: 'white' }}>
                    <i className={viewSalesOnly ? 'feather-eye me-2' : 'feather-dollar-sign me-2'}></i>
                    {viewSalesOnly 
                      ? (selectedProject?.projectName ? `View Sales - ${selectedProject.projectName}` : 'View Sales')
                      : (selectedProject?.projectName ? `Token Sale - ${selectedProject.projectName}` : 'Add Token Sale')}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '1.5rem' }}>
                  {selectedProject && (
                    <>
                      {/* Project Info Cards */}
                      <div className="row mb-4">
                        <div className="col-md-6 mb-3">
                          <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <div className="text-muted mb-1" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <i className="feather-unlock me-1"></i>Released Tokens
                                  </div>
                                  <div className="fw-bold" style={{ fontSize: '20px', color: '#2c3e50' }}>
                                    {selectedProject.tokensReleased || 0}
                                  </div>
                                </div>
                                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
                                  <i className="feather-unlock text-primary" style={{ fontSize: '20px' }}></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' }}>
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <div className="text-muted mb-1" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <i className="feather-clock me-1"></i>Remaining Tokens
                                  </div>
                                  <div className="fw-bold" style={{ fontSize: '20px', color: '#2c3e50' }}>
                                    {(selectedProject.remainingTokens || 0).toFixed(2)}
                                    {selectedProject.tokensReleased > 0 && (
                                      <span className="text-muted ms-2" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                                        ({((selectedProject.remainingTokens || 0) / selectedProject.tokensReleased * 100).toFixed(1)}%)
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
                                  <i className="feather-clock text-warning" style={{ fontSize: '20px' }}></i>
                                </div>
                              </div>
                            </div>
                          </div>
                   </div>
                 </div>

                      {/* Existing Sales Table - always show when view-only; otherwise show if there are sales */}
                      {(viewSalesOnly || tokenSales.filter(x => x.proj_id == selectedProject.id).length > 0) && (
                        <div className="mb-4">
                          <h6 className="fw-bold mb-3" style={{ fontSize: '14px', color: '#2c3e50' }}>
                            <i className="feather-list me-2 text-primary"></i>{viewSalesOnly ? 'All Sales' : 'Previous Sales'}
                          </h6>
                          <div className="table-responsive" style={{ maxHeight: viewSalesOnly ? '400px' : '220px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <table className="table table-hover mb-0">
                              <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                                <tr>
                                  <th className="px-3 py-2 fw-semibold" style={{ fontSize: '12px', color: '#6c757d' }}>Tokens</th>
                                  <th className="px-3 py-2 fw-semibold text-end" style={{ fontSize: '12px', color: '#6c757d' }}>Amount</th>
                                  <th className="px-3 py-2 fw-semibold text-center" style={{ fontSize: '12px', color: '#6c757d' }}>Date</th>
                                  <th className="px-3 py-2 fw-semibold" style={{ fontSize: '12px', color: '#6c757d' }}>Comment</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tokenSales.filter(x => x.proj_id == selectedProject.id).length > 0 ? (
                                  tokenSales.filter(x => x.proj_id == selectedProject.id).map((tokenSale, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                      <td className="px-3 py-2">
                                        <span className="fw-medium" style={{ fontSize: '13px', color: '#2c3e50' }}>
                                          {parseFloat(tokenSale.tokens_sold).toLocaleString()}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-end">
                                        <span className="fw-bold text-success" style={{ fontSize: '13px' }}>
                                          ${parseFloat(tokenSale.sale_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <span className="text-muted" style={{ fontSize: '12px' }}>
                                          {moment(tokenSale.sale_date).format('MMM DD, YYYY')}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2">
                                        {renderCommentWithLinks(tokenSale.comment)}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={4} className="px-3 py-4 text-center text-muted" style={{ fontSize: '13px' }}>No sales recorded yet.</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Form Section - hide when view-only */}
                      {!viewSalesOnly && (
                      <div style={{ borderTop: '2px solid #e9ecef', paddingTop: '1.5rem' }}>
                        <h6 className="fw-bold mb-3" style={{ fontSize: '14px', color: '#2c3e50' }}>
                          <i className="feather-plus-circle me-2 text-success"></i>Add New Sale
                        </h6>
                        
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-hash me-1 text-primary"></i>Tokens Sold
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Enter tokens sold"
                              value={projTokensSold}
                              onChange={(e) => {
                                const maxTokens = selectedProject.remainingTokens || 0;
                                const value = parseFloat(e.target.value) || 0;
                                if (value <= maxTokens) {
                                  setProjTokensSold(e.target.value);
                                } else {
                                  setProjTokensSold(maxTokens.toString());
                                }
                              }}
                              max={selectedProject.remainingTokens || 0}
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            />
                            <small className="text-muted" style={{ fontSize: '11px' }}>
                              Max: {(selectedProject.remainingTokens || 0).toFixed(2)} tokens
                            </small>
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-dollar-sign me-1 text-success"></i>Sale Amount ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              placeholder="Enter sale amount"
                              value={projSoldAmount}
                              onChange={(e) => setProjSoldAmount(e.target.value)}
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-calendar me-1 text-info"></i>Sale Date
                            </label>
                            <DatePicker
                              selected={projSaleDate}
                              onChange={(date) => setProjSaleDate(date)}
                              dateFormat="MMM dd, yyyy"
                              className="form-control"
                              placeholderText="Select sale date"
                              wrapperClassName="w-100"
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px'
                              }}
                            />
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-message-circle me-1 text-secondary"></i>Comment (Optional)
                            </label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={projSaleComment}
                              onChange={(e) => setProjSaleComment(e.target.value)}
                              placeholder="Add any additional notes..."
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px',
                                resize: 'none',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      )}
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '1rem 1.5rem', background: '#f8f9fa' }}>
                  <div className="d-flex justify-content-end gap-2 w-100">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => { setProjectShow(false); setViewSalesOnly(false); }}
                      style={{
                        borderRadius: '8px',
                        padding: '8px 20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        border: '1px solid #dee2e6',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e9ecef';
                        e.currentTarget.style.borderColor = '#adb5bd';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#dee2e6';
                      }}
                    >
                      <i className="feather-x me-1"></i>{viewSalesOnly ? 'Close' : 'Cancel'}
                    </button>
                    {selectedProject && selectedProject.remainingTokens > 0 && !viewSalesOnly && (() => {
                      const loginData = (authStorage.getLoginData() || {});
                      return loginData.id == 1 ? (
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={saveTokenSale}
                          style={{
                            borderRadius: '8px',
                            padding: '8px 24px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
                          }}
                        >
                          <i className="feather-check me-1"></i>Submit
                        </button>
                      ) : null;
                    })()}
                  </div>
                </Modal.Footer>
              </Modal>

              {/* Add Spot Sale Modal */}
              <Modal 
                show={spotSaleShow} 
                onHide={() => setSpotSaleShow(false)} 
                animation={true}
                size="lg"
                centered
                style={{ zIndex: 1055 }}
              >
                <style>{`
                  .modal.show .modal-header .btn-close {
                    filter: brightness(0) invert(1);
                    opacity: 1;
                  }
                `}</style>
                <Modal.Header 
                  closeButton 
                  style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderBottom: 'none',
                    padding: '1.25rem 1.5rem'
                  }}
                >
                  <Modal.Title style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: 'white' }}>
                    <i className="feather-dollar-sign me-2"></i>
                    {selectedTokenData?.token_name ? `Spot Sale - ${selectedTokenData.token_name}` : 'Add Spot Sale'}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '1.5rem' }}>
                  {selectedTokenData && (
                    <>
                      {/* Token Info Cards */}
                      <div className="row mb-4">
                        <div className="col-md-6 mb-3">
                          <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <div className="text-muted mb-1" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <i className="feather-coins me-1"></i>Total Tokens
                                  </div>
                                  <div className="fw-bold" style={{ fontSize: '20px', color: '#2c3e50' }}>
                                    {parseFloat(selectedTokenData.no_of_tokens || 0).toLocaleString()}
                                  </div>
                                </div>
                                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
                                  <i className="feather-coins text-primary" style={{ fontSize: '20px' }}></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="card border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' }}>
                            <div className="card-body p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <div className="text-muted mb-1" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <i className="feather-clock me-1"></i>Remaining Tokens
                                  </div>
                                  <div className="fw-bold" style={{ fontSize: '20px', color: '#2c3e50' }}>
                                    {(() => {
                                      const spotSales = spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id);
                                      const tokensSold = spotSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
                                      const remaining = parseFloat(selectedTokenData.no_of_tokens || 0) - tokensSold;
                                      return remaining.toFixed(2);
                                    })()}
                                    {selectedTokenData.no_of_tokens > 0 && (
                                      <span className="text-muted ms-2" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                                        ({(() => {
                                          const spotSales = spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id);
                                          const tokensSold = spotSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
                                          const remaining = parseFloat(selectedTokenData.no_of_tokens || 0) - tokensSold;
                                          return ((remaining / parseFloat(selectedTokenData.no_of_tokens || 1)) * 100).toFixed(1);
                                        })()}%)
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
                                  <i className="feather-clock text-warning" style={{ fontSize: '20px' }}></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Token Details */}
                      <div className="row mb-4">
                        <div className="col-md-6 mb-3">
                          <div className="d-flex justify-content-between align-items-center p-3" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
                            <span className="text-muted" style={{ fontSize: '13px' }}>Buying Price:</span>
                            <span className="fw-semibold" style={{ fontSize: '14px', color: '#2c3e50' }}>
                              ${parseFloat(selectedTokenData.token_price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="d-flex justify-content-between align-items-center p-3" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
                            <span className="text-muted" style={{ fontSize: '13px' }}>Invested Amount:</span>
                            <span className="fw-semibold" style={{ fontSize: '14px', color: '#2c3e50' }}>
                              ${parseFloat(selectedTokenData.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="d-flex justify-content-between align-items-center p-3" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
                            <span className="text-muted" style={{ fontSize: '13px' }}>Market Price:</span>
                            <span className="fw-semibold" style={{ 
                              fontSize: '14px',
                              color: (selectedTokenData.current_price || 0) > parseFloat(selectedTokenData.token_price || 0) ? '#28a745' : '#dc3545'
                            }}>
                              ${(selectedTokenData.current_price || 0).toFixed(3)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Existing Sales Table */}
                      {spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id).length > 0 && (
                        <div className="mb-4">
                          <h6 className="fw-bold mb-3" style={{ fontSize: '14px', color: '#2c3e50' }}>
                            <i className="feather-list me-2 text-primary"></i>Previous Sales
                          </h6>
                          <div className="table-responsive" style={{ maxHeight: '220px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <table className="table table-hover mb-0">
                              <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                                <tr>
                                  <th className="px-3 py-2 fw-semibold" style={{ fontSize: '12px', color: '#6c757d' }}>Tokens</th>
                                  <th className="px-3 py-2 fw-semibold text-end" style={{ fontSize: '12px', color: '#6c757d' }}>Amount</th>
                                  <th className="px-3 py-2 fw-semibold text-center" style={{ fontSize: '12px', color: '#6c757d' }}>Date</th>
                                  <th className="px-3 py-2 fw-semibold" style={{ fontSize: '12px', color: '#6c757d' }}>Comment</th>
                                </tr>
                              </thead>
                              <tbody>
                                {spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id).map((tokenSale, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td className="px-3 py-2">
                                      <span className="fw-medium" style={{ fontSize: '13px', color: '#2c3e50' }}>
                                        {parseFloat(tokenSale.tokens_sold).toLocaleString()}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-end">
                                      <span className="fw-bold text-success" style={{ fontSize: '13px' }}>
                                        ${parseFloat(tokenSale.selling_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <span className="text-muted" style={{ fontSize: '12px' }}>
                                        {moment(tokenSale.selling_date).format('MMM DD, YYYY')}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2">
                                      {renderCommentWithLinks(tokenSale.comment)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Form Section */}
                      <div style={{ borderTop: '2px solid #e9ecef', paddingTop: '1.5rem' }}>
                        <h6 className="fw-bold mb-3" style={{ fontSize: '14px', color: '#2c3e50' }}>
                          <i className="feather-plus-circle me-2 text-success"></i>Add New Sale
                        </h6>
                        
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-hash me-1 text-primary"></i>Tokens Sold
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Enter tokens sold"
                              value={spotTokensSold}
                              onChange={(e) => {
                                const spotSales = spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id);
                                const tokensSold = spotSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
                                const maxTokens = parseFloat(selectedTokenData.no_of_tokens || 0) - tokensSold;
                                const value = parseFloat(e.target.value) || 0;
                                if (value <= maxTokens) {
                                  setSpotTokensSold(e.target.value);
                                } else {
                                  setSpotTokensSold(maxTokens.toString());
                                }
                              }}
                              max={(() => {
                                const spotSales = spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id);
                                const tokensSold = spotSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
                                return parseFloat(selectedTokenData.no_of_tokens || 0) - tokensSold;
                              })()}
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            />
                            <small className="text-muted" style={{ fontSize: '11px' }}>
                              Max: {(() => {
                                const spotSales = spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id);
                                const tokensSold = spotSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
                                return (parseFloat(selectedTokenData.no_of_tokens || 0) - tokensSold).toFixed(2);
                              })()} tokens
                            </small>
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-dollar-sign me-1 text-success"></i>Sale Amount ($)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-control"
                              placeholder="Enter sale amount"
                              value={spotSoldAmount}
                              onChange={(e) => setSpotSoldAmount(e.target.value)}
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-calendar me-1 text-info"></i>Sale Date
                            </label>
                            <DatePicker
                              selected={spotSaleDate}
                              onChange={(date) => setSpotSaleDate(date)}
                              dateFormat="MMM dd, yyyy"
                              className="form-control"
                              placeholderText="Select sale date"
                              wrapperClassName="w-100"
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px'
                              }}
                            />
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold mb-2" style={{ fontSize: '13px', color: '#495057' }}>
                              <i className="feather-message-circle me-1 text-secondary"></i>Comment (Optional)
                            </label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={spotSaleComment}
                              onChange={(e) => setSpotSaleComment(e.target.value)}
                              placeholder="Add any additional notes..."
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #dee2e6',
                                padding: '10px 14px',
                                fontSize: '14px',
                                resize: 'none',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.15)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#dee2e6';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e9ecef', padding: '1rem 1.5rem' }}>
                  <div className="d-flex justify-content-end gap-2 w-100">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => setSpotSaleShow(false)}
                      style={{
                        borderRadius: '8px',
                        padding: '8px 24px',
                        fontSize: '12px',
                        fontWeight: '500',
                        border: '1px solid #dee2e6',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e9ecef';
                        e.currentTarget.style.borderColor = '#adb5bd';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#dee2e6';
                      }}
                    >
                      <i className="feather-x me-1"></i>Cancel
                    </button>
                    {selectedTokenData && (() => {
                      const spotSales = spotTokenSales.filter(x => x.token_id == selectedTokenData.token_id);
                      const tokensSold = spotSales.reduce((sum, sale) => sum + parseFloat(sale.tokens_sold || 0), 0);
                      const remainingTokens = parseFloat(selectedTokenData.no_of_tokens || 0) - tokensSold;
                      const loginData = (authStorage.getLoginData() || {});
                      return remainingTokens > 0 && loginData.id == 1 ? (
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={saveSpotSale}
                          style={{
                            borderRadius: '8px',
                            padding: '8px 24px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
                          }}
                        >
                          <i className="feather-check me-1"></i>Submit
                        </button>
                      ) : null;
                    })()}
                  </div>
                </Modal.Footer>
              </Modal>

                   </div>
                 </div>

               </div>
             </div>

           </main>
           </div>
         );
       };

       export default LPDashboard;

